import "./styles.css";
import { summarizeProjectionCoverage, type ProjectionCoverage } from "./domain/coverage";
import { anonymousFixture } from "./domain/fixture";
import { buildCanonicalProposal, outcomeForElement } from "./domain/proposal";
import {
  emptyDraftLedger,
  removeDraftChange,
  stageBulkPropertyChange,
  stageDraftChange,
  type DraftEntry,
  type DraftOperation,
  type DraftSource
} from "./domain/draft";
import {
  createFixtureDatasetIndex,
  queryFixtureDataset,
  selectedFixtureElements,
  type FixtureDatasetIndex
} from "./domain/dataset-index";
import type { AnonymousFixture, FixtureElement, MappingRequest, Outcome } from "./domain/types";
import {
  currentEditablePropertyValue,
  editablePropertyOptions,
  inspectElements
} from "./domain/inspection";
import type { LocalIfcProjection } from "./domain/local-ifc-projection";
import { buildInspectionHierarchy, type InspectionHierarchyNode } from "./domain/hierarchy";
import {
  addDisplayColumn,
  addGroupingRule,
  availableDisplayColumns,
  availableGroupingKeys,
  buildReport,
  DEFAULT_REPORT_GROUPING_RULES,
  formatReportColumnValue,
  groupingKeyLabel,
  moveGroupingRule,
  removeDisplayColumn,
  removeGroupingRule,
  reportToCsv,
  type Report,
  type ReportGroupingKeyDescriptor,
  type ReportGroupingKeyId
} from "./domain/report";
import {
  planMissingPropertyDraft,
  qualityIssuePage,
  summarizeQualityIssues,
  type QualitySeverity,
  type QualitySummary
} from "./domain/quality";
import { LOCAL_IFC_ARTIFACT, type LocalIfcObservation } from "./integration/local-ifc";
import { DEFAULT_IFC_CAPABILITY_PROVIDER } from "./integration/ifc-capability-provider";
import { mergeMeshGeometryIntoFixture, type MeshFixtureMergeAccounting } from "./domain/ifc-mesh-import";
import { extractLocalIfcMesh, LOCAL_IFC_MESH_ARTIFACT, type MeshExtractionOutcome } from "./integration/ifc-mesh-extraction";
import {
  authorLocalClassificationProposalFromStaged,
  authorLocalEnrichmentProposalFromDraft,
  localSourceBinding,
  type LocalProposalBinding,
  type LocalSourceBinding
} from "./integration/local-proposal-binding";
import type { D2LocalEnrichmentProposal } from "./domain/d2-local-enrichment-proposal";
import type { D2LocalClassificationProposal, D2LocalClassificationReference } from "./domain/d2-local-classification-proposal";
import {
  buildD3CommitPayload,
  type D3CommitPayload,
  type D3CommitPayloadResult,
  type D3ProposalEntryInput
} from "./domain/d3-commit-payload";
import { buildModelStoragePlan, hydrateAndUpdateModelStoragePlan, type ModelStorageResult } from "./integration/bim-hub-model-storage";
import { base64ToBytes, bytesToBase64 } from "./integration/binary-base64";
import {
  loadKnownD3SaveTarget,
  persistKnownD3SaveTarget,
  type D3KnownSaveTarget
} from "./integration/d3-save-persistence";
import { diffModelChunkPages, parseModelChunkManifest, type ModelPageChanges } from "./domain/model-chunk-manifest";
import type { D3EnsureTargetResult, D3ReadModelSnapshotResult, D3SaveResult } from "./server/d3-save";
import type { PropertyProposalSaveResult } from "./server/property-proposal-save";
import { buildPropertyProposalRequest } from "./domain/property-proposal-request";
import { evaluateDraftEntryForProposal } from "./domain/property-proposal-from-draft";
import {
  regenerateLocalIfcProjection,
  type ProjectionRegenerationResult
} from "./integration/local-ifc-regeneration";
import { localIfcDisplayReceipt } from "./integration/local-ifc-receipt";
import {
  saveNativeLocalExport,
  type LocalExportArtifact,
  type LocalExportSaveOutcome
} from "./integration/local-export-save";
import {
  clearNativeDraftLedger,
  loadNativeDraftLedger,
  persistNativeDraftLedger
} from "./integration/local-draft-persistence";
import {
  exportNativeLocalDirectory,
  type DirectoryExportArtifact,
  type DirectoryExportOutcome
} from "./integration/local-directory-export";
import { isNativeOpenShortcut } from "./integration/native-shell-shortcut";
import { buildT0ParitySnapshot, type T0ParitySnapshot } from "./integration/t0-parity";
import {
  isNativeLocalShell,
  pickNativeIfcSource,
  sourceFromBrowserFile,
  type LocalSourceShell,
  type SelectedLocalSource
} from "./integration/local-source";
import { OUTCOMES } from "./domain/types";
import { sharedLibraryAccess } from "./domain/tool-access";
import {
  evaluateLocalValidation,
  localValidationRules,
  planLocalValidationOutcome,
  summarizeLocalValidation,
  summarizeLocalValidationByType,
  type LocalValidationOutcome,
  type LocalValidationOutcomePlan,
  type LocalValidationOverview,
  type LocalValidationResult,
  type LocalValidationTypeSummary
} from "./domain/validation";
import { formatBytes, formatMilliseconds, RuntimeMeter } from "./ui/metrics";
import { FixtureScene, type SceneInteractionMode } from "./ui/scene";
import {
  defaultWorkspaceShellState,
  openInspectorForTool,
  reconcileWorkspaceForLayout,
  toggleWorkspacePane,
  type WorkspaceLayout,
  type WorkspaceShellState
} from "./ui/workspace-shell";
import { AuthFlow, type AuthClient } from "./auth/auth-client";
import { AnonymousAuthClient } from "./auth/anonymous-auth-client";
import { BrowserIdentityClient } from "./auth/browser-identity-client";
import { identityConsoleUrl } from "./auth/identity-console-link";
import { authAccountButtonAriaLabel, authAccountButtonLabel } from "./auth/auth-account-presentation";
import { authReturnTo, type AuthState, type AuthToolId } from "./auth/auth-state";

declare global {
  interface Window {
    d0RuntimeMetrics?: () => ReturnType<RuntimeMeter["snapshot"]>;
    aecLocalIfcObservation?: () => LocalIfcObservation | null;
    aecLocalShellMetrics?: () => LocalShellMetrics | null;
    aecSceneViewState?: () => ReturnType<FixtureScene["viewState"]>;
    aecProjectionCoverage?: () => ProjectionCoverage;
    aecLocalIfcMeshExtraction?: () => MeshExtractionOutcome | null;
    aecLocalIfcMeshAccounting?: () => MeshFixtureMergeAccounting | null;
    aecAuthState?: () => AuthState;
    __aecTestAuthClient?: AuthClient;
  }
}

interface LocalShellMetrics {
  readonly shell: LocalSourceShell;
  readonly lifecycle: "observed" | "refused" | "cleared";
  readonly openCount: number;
  readonly fileReadMs: number;
  readonly importerInitializationMs: number;
  readonly observationMs: number;
  readonly projectionMs: number | null;
  readonly meshExtractionMs: number | null;
  readonly wasmLinearMemoryBytes: number | null;
  readonly jsHeapBytes: number | null;
  readonly sourceBytesRetained: number;
  readonly paritySnapshot: T0ParitySnapshot | null;
}

const rowHeight = 44;
const rowOverscan = 6;
const applicationStartedAt = performance.now();
const runtimeMeter = new RuntimeMeter();
const nativeLocalShell = isNativeLocalShell();
const defaultRequest: MappingRequest = { property: "FireRating", value: "60min" };
const state = {
  selectedIds: new Set<string>(),
  primarySelectedId: null as string | null,
  selectionAnchorId: null as string | null,
  filter: "",
  typeFilter: null as string | null,
  outcomeFilter: null as Outcome | null,
  request: { ...defaultRequest },
  activeTool: "inspect" as "inspect" | "codes" | "components" | "quality" | "report",
  draft: emptyDraftLedger(anonymousFixture.id),
  qualitySeverity: "all" as QualitySeverity | "all",
  qualityAction: "",
  bulkPropertyAction: "",
  classificationAction: "",
  hierarchyFocus: null as { readonly nodeId: string; readonly label: string; readonly elementIds: ReadonlySet<string> } | null
};
let currentFixture = anonymousFixture;
let currentDatasetIndex: FixtureDatasetIndex = createFixtureDatasetIndex(anonymousFixture.elements);
let currentVisibleElements: readonly FixtureElement[] = anonymousFixture.elements;
let currentQuality: LocalIfcProjection["quality"] | null = null;
let currentQualitySummary: QualitySummary = summarizeQualityIssues([], anonymousFixture.elements);
let currentProjectionCoverage: ProjectionCoverage = summarizeProjectionCoverage(anonymousFixture.elements);
let currentLocalIfcObservation: LocalIfcObservation | null = null;
// Additive to the plan-derived box projection above (see plan/roadmap.md D0, 2026-08-11 addendum):
// best-effort tessellated mesh extraction for the same observed source bytes. `null` before any
// local source has been observed; a typed "refused" outcome (not a thrown error) when the mesh
// engine could not extract geometry for this source, leaving the box projection as the sole
// renderable geometry -- see src/domain/ifc-mesh-import.ts's mergeMeshGeometryIntoFixture.
let currentMeshExtraction: MeshExtractionOutcome | null = null;
let currentMeshAccounting: MeshFixtureMergeAccounting | null = null;
let currentLocalSource: SelectedLocalSource | null = null;
let currentRegeneratedIfc: ProjectionRegenerationResult | null = null;
let localExportState: "idle" | "working" | "ready" | "error" = "idle";
let localExportMessage = "";
let localSaveWorking = false;
let directoryExportMessage = "";
let directoryExportWorking = false;
let currentValidationResult: LocalValidationResult | null = null;
let currentValidationOverview: LocalValidationOverview | null = null;
let currentValidationTypeSummary: readonly LocalValidationTypeSummary[] = summarizeLocalValidationByType(anonymousFixture.elements);
let currentHierarchy: InspectionHierarchyNode = buildInspectionHierarchy(
  anonymousFixture.id,
  anonymousFixture.label,
  anonymousFixture.elements
);
let reportGroupingRules: readonly ReportGroupingKeyId[] = DEFAULT_REPORT_GROUPING_RULES;
// Display columns are a distinct concept from grouping rules (see domain/report.ts): they show a
// chosen property's value on each already-grouped row without changing how records are
// partitioned, so they get their own state rather than piggybacking on reportGroupingRules.
let reportDisplayColumns: readonly string[] = [];
let currentReport: Report = buildReport(
  anonymousFixture.id,
  anonymousFixture.label,
  anonymousFixture.elements,
  null,
  reportGroupingRules,
  reportDisplayColumns
);
let expandedHierarchyIds = new Set<string>([
  currentHierarchy.id,
  ...currentHierarchy.children.map((scope) => scope.id)
]);
let currentValidationFocus: {
  readonly ruleId: string;
  readonly outcome: LocalValidationOutcome;
  readonly elementIds: ReadonlySet<string>;
  readonly plan: LocalValidationOutcomePlan;
} | null = null;

// workbench-local-enrichment-proposal/1 bound to the active Draft ledger and local IFC source receipt.
let currentEnrichmentBinding: LocalSourceBinding = { bound: false };
let currentEnrichmentProposal: D2LocalEnrichmentProposal | null = null;
let enrichmentProposalSignature: string | null = null;
let enrichmentProposalToken = 0;

// workbench-local-classification-proposal/1 staged from the Codes tool, bound to the same source receipt.
let classificationStagedAfter = new Map<string, D2LocalClassificationReference>();
let currentClassificationBinding: LocalSourceBinding = { bound: false };
let currentClassificationProposal: D2LocalClassificationProposal | null = null;
let classificationProposalSignature: string | null = null;
let classificationProposalToken = 0;

// D3 commit payload preview: built only once both proposals above have resolved for the same
// bound source. This is a review-only artifact (`transmitted: false`) — no network call is made
// anywhere in this app; see `domain/d3-commit-payload.ts` for why.
let currentD3CommitPayload: D3CommitPayloadResult | null = null;
let d3CommitPayloadSignature: string | null = null;
let d3CommitPayloadToken = 0;

// D3 first live save (see bim-hub-integration/answers/2026-08-11-d3-first-live-save-go.md). This is
// distinct from `currentD3CommitPayload` above, which only ever describes a locally-built,
// not-yet-sent preview: `d3SaveState`/`d3SaveReceipt`/`d3SaveError` describe what actually happened
// on the wire the last time this fixture's Send button was used. `d3SaveReceipt` is a server fact
// (BIM-Hub's own committed revision/digests, echoed back by `functions/d3/save.ts`) and is rendered
// in a visually distinct block from the "not sent" / unsaved-proposal state so the two are never
// conflated (condition 3 of the GO answer).
type D3SaveUiState = "idle" | "saving" | "saved" | "error";
let d3SaveState: D3SaveUiState = "idle";
let d3SaveReceipt: Extract<D3SaveResult, { readonly ok: true }> | null = null;
let d3SaveError: { readonly code: string; readonly message: string } | null = null;
let d3SaveToken = 0;

// D2-C property proposal -> BIM-Hub (see bim-hub-integration/answers/
// 2026-08-13-property-proposal-connection-go.md). Distinct from D3 save above: a property proposal
// targets ONE staged replace (`DraftEntry`), not the whole draft ledger, and reuses D3 save's own
// known Project/Model/revision (`loadKnownD3SaveTarget`) rather than establishing its own -- a
// proposal needs an existing `base_revision` to propose against, so it is only ever offered once at
// least one D3 save has succeeded for this fixture. State is keyed per entry
// (`propertyProposalEntryKey`), not a single global state, since multiple pending changes can each
// be proposed independently.
type PropertyProposalOutcome =
  | { readonly ok: true; readonly status: "committed" | "idempotent-replay"; readonly targetRevision: number; readonly manifestSha256: string }
  | { readonly ok: false; readonly code: string; readonly message: string };
const propertyProposalSubmitting = new Set<string>();
const propertyProposalOutcomes = new Map<string, PropertyProposalOutcome>();

function propertyProposalEntryKey(elementId: string, property: string): string {
  return `${elementId}\u0000${property}`;
}

// Tauri-only device-local recovery cache for `state.draft` (see `integration/local-draft-persistence.ts`).
// This is an ephemeral Workbench UI preference, never model truth or an authoritative BIM-Hub cache.
let draftPersistenceSignature: string | null = null;
let draftPersistenceToken = 0;

const app = document.querySelector<HTMLDivElement>("#app");
if (app === null) {
  throw new Error("Application root is missing.");
}

app.innerHTML = `
  <header class="app-header">
    <div class="app-identity">
      <strong>AEC Data Workbench</strong>
      <span id="active-source-label">${anonymousFixture.label}</span>
    </div>
    <div class="header-actions">
      <button id="open-ifc" class="primary-action" type="button"><span class="button-spinner" aria-hidden="true"></span><span id="open-ifc-label">Open IFC</span></button>
      <button id="open-export" type="button" aria-haspopup="dialog">Export</button>
      <button id="open-source-details" type="button">Source details</button>
      <button id="coverage-trigger" class="header-text-action" type="button" aria-haspopup="dialog"><span id="scene-coverage-compact-summary">360 shown in 3D</span></button>
      <button id="open-measurements" class="header-text-action debug-action" type="button" aria-haspopup="dialog">debug</button>
      <button id="auth-account" type="button" aria-haspopup="dialog">Sign in</button>
      ${nativeLocalShell ? '<div class="shell-status"><strong>Native Local</strong><span>local · unshared · unsynced</span></div>' : ""}
    </div>
  </header>
  <main>
    <section id="workspace" class="workspace" aria-label="Fixture inspection workspace">
      <section id="model-browser-panel" class="model-browser-panel panel" aria-label="Model browser">
        <div class="panel-heading"><h2>Browse</h2><div class="panel-heading-actions"><span id="table-summary" hidden></span><button id="close-browser" class="panel-close" type="button" aria-label="Close Model browser">×</button></div></div>
        <div class="controls">
          <input id="filter" type="search" placeholder="Search records" aria-label="Filter records" />
          <button id="select-visible" type="button">Select visible</button>
          <button id="clear-selection" type="button" aria-label="Clear selection">Clear</button>
        </div>
        <div id="model-hierarchy" class="model-hierarchy" role="tree" aria-label="Model hierarchy and elements">
          <div id="virtual-table" class="virtual-table hierarchy-element-group" role="group" aria-label="Elements"><div id="virtual-rows"></div></div>
        </div>
      </section>
      <div id="scene-panel" class="scene-panel panel">
        <div id="scene" class="scene" aria-label="Simplified 3D fixture scene">
          <span id="scene-summary" hidden>360 simplified fixture boxes</span>
          <nav class="scene-browse-rail" aria-label="Dataset views">
            <button id="toggle-browser" class="scene-browser-toggle" type="button" aria-controls="model-browser-panel" aria-expanded="true">Browse</button>
            <button type="button" data-tool="report" aria-controls="inspector-panel" title="Open Report">Report</button>
          </nav>
          <nav class="scene-tool-rail" aria-label="Inspection tools">
            <button type="button" data-tool="inspect" aria-controls="inspector-panel" title="Open Inspect">Inspect</button>
            <button type="button" data-tool="codes" aria-controls="inspector-panel" title="Open Codes">Codes</button>
            <button type="button" data-tool="components" aria-controls="inspector-panel" title="Open Components">Components</button>
            <button type="button" data-tool="quality" aria-controls="inspector-panel" title="Open local checks">Checks</button>
          </nav>
          <nav class="scene-camera-dock" aria-label="Camera and selection controls">
            <button id="frame-all" type="button" title="Frame all supported 3D representations"><span aria-hidden="true">⌂</span><span>Frame all</span></button>
            <button id="restore-view" type="button" title="Return to the view before the last focus" disabled><span aria-hidden="true">↩</span><span>Back view</span></button>
            <button id="navigate-mode" type="button" aria-pressed="true" title="Orbit, pan, and zoom"><span aria-hidden="true">↻</span><span>Navigate</span></button>
            <button id="pan-mode" type="button" aria-pressed="false" title="Pan without orbiting"><span aria-hidden="true">✥</span><span>Pan</span></button>
            <button id="select-mode" type="button" aria-pressed="false" title="Click or drag to toggle 3D elements"><span aria-hidden="true">↖</span><span>Select</span></button>
            <button id="clear-scene-selection" type="button" title="Clear the current selection"><span aria-hidden="true">×</span><span>Clear</span></button>
          </nav>
        </div>
      </div>
      <section id="inspector-panel" class="inspector-panel panel" aria-label="Inspector">
        <div id="inspector-panel-heading" class="panel-heading"><h2 id="inspector-title">Inspect</h2><div class="panel-heading-actions"><span id="selected-summary"></span><button id="close-inspector" class="panel-close" type="button" aria-label="Close Inspector">×</button></div></div>
        <div id="tool-content" class="tool-content"></div>
        <div id="report-resize-handle" class="panel-resize-handle" aria-hidden="true" hidden></div>
        <div id="draft-ledger" class="draft-ledger"></div>
        <details class="inspector-proposal">
          <summary><span>Change preview</span><small>Local session</small></summary>
          <div class="proposal-controls">
            <label>Property <input id="property" value="FireRating" aria-label="Property name" /></label>
            <label>Value <input id="value" value="60min" aria-label="Property value" /></label>
            <button id="preview" type="button">Generate preview</button>
            <button id="stage-draft" type="button">Add pending change</button>
          </div>
          <div class="validation-state"><strong>Validation: partial</strong><span>Local evidence only; not an authoritative verdict.</span></div>
          <pre id="proposal-output" data-state="idle" aria-live="polite">Select one or more fixture elements to generate a before/after proposal.</pre>
        </details>
      </section>
    </section>
  </main>
  <dialog id="auth-dialog" class="utility-dialog auth-dialog" aria-labelledby="auth-dialog-title">
    <div class="utility-heading">
      <div><p class="eyebrow">Cloud workspace</p><h2 id="auth-dialog-title">Sign in</h2></div>
      <button class="panel-close" type="button" data-close-dialog="auth-dialog" aria-label="Close Sign in">×</button>
    </div>
    <div id="auth-content" class="utility-content auth-content" aria-live="polite"></div>
  </dialog>
  <dialog id="export-dialog" class="utility-dialog export-dialog" aria-labelledby="export-dialog-title">
    <div class="utility-heading">
      <div><p class="eyebrow">Local model</p><h2 id="export-dialog-title">Export IFC</h2></div>
      <button class="panel-close" type="button" data-close-dialog="export-dialog" aria-label="Close Export IFC">×</button>
    </div>
    <div id="local-export-surface" class="utility-content"></div>
  </dialog>
  <dialog id="source-dialog" class="utility-dialog" aria-labelledby="source-dialog-title">
    <div class="utility-heading">
      <div><p class="eyebrow">Local session</p><h2 id="source-dialog-title">Source details</h2></div>
      <button class="panel-close" type="button" data-close-dialog="source-dialog" aria-label="Close Source details">×</button>
    </div>
    <section class="local-ifc-panel utility-content" aria-label="Local IFC observation">
      <div class="panel-heading"><h2>Local IFC observation</h2><span>${nativeLocalShell ? "Native Local · read-only" : "Read-only · browser session only"}</span></div>
      <div class="local-ifc-controls">
        <label ${nativeLocalShell ? "hidden" : ""}>IFC source <input id="local-ifc-file" type="file" accept=".ifc,application/x-step" aria-label="Local IFC source" /></label>
        <button id="observe-local-ifc" type="button">${nativeLocalShell ? "Choose and observe IFC" : "Observe local IFC"}</button>
        <button id="restore-fixture" type="button">Use sample fixture</button>
      </div>
      <p class="local-ifc-note">Uses pinned ${LOCAL_IFC_ARTIFACT.packageName} ${LOCAL_IFC_ARTIFACT.version} (${LOCAL_IFC_ARTIFACT.wasmDigest.slice(0, 12)}…). Sources remain local. Supported property edits can be regenerated as a local IFC; no cloud save occurs.</p>
      <p id="local-ifc-projection-state" class="local-ifc-projection-state">The sample fixture remains active until a local observation succeeds.</p>
      <pre id="local-ifc-output" data-state="idle" aria-live="polite">Choose a local IFC file to create a read-only observation.</pre>
      <section id="import-diagnostics" class="import-diagnostics" aria-label="Import diagnostics"></section>
      <pre id="local-shell-metrics" class="local-shell-metrics" data-state="idle" aria-live="polite">No local source measurement yet.</pre>
    </section>
  </dialog>
  <dialog id="measurements-dialog" class="utility-dialog" aria-labelledby="measurements-dialog-title">
    <div class="utility-heading">
      <div><p class="eyebrow">Diagnostics</p><h2 id="measurements-dialog-title">Runtime measurements</h2></div>
      <button class="panel-close" type="button" data-close-dialog="measurements-dialog" aria-label="Close Runtime measurements">×</button>
    </div>
    <section class="metrics-panel utility-content" aria-label="Runtime measurements">
      <dl id="runtime-metrics" class="runtime-metrics"></dl>
    </section>
  </dialog>
  <dialog id="coverage-dialog" class="utility-dialog coverage-dialog" aria-labelledby="coverage-dialog-title">
    <div class="utility-heading">
      <div><p class="eyebrow">Model evidence</p><h2 id="coverage-dialog-title">Geometry coverage</h2></div>
      <button class="panel-close" type="button" data-close-dialog="coverage-dialog" aria-label="Close Geometry coverage">×</button>
    </div>
    <div id="scene-coverage" class="scene-coverage utility-content" aria-live="polite"></div>
  </dialog>
`;

const sceneContainer = requiredElement<HTMLDivElement>("scene");
const workspace = requiredElement<HTMLElement>("workspace");
const scenePanel = requiredElement<HTMLElement>("scene-panel");
const modelBrowserPanel = requiredElement<HTMLElement>("model-browser-panel");
const inspectorPanel = requiredElement<HTMLElement>("inspector-panel");
const inspectorPanelHeading = requiredElement<HTMLElement>("inspector-panel-heading");
const reportResizeHandle = requiredElement<HTMLElement>("report-resize-handle");
const modelHierarchy = requiredElement<HTMLDivElement>("model-hierarchy");
const sceneSummary = requiredElement<HTMLSpanElement>("scene-summary");
const sceneCoverage = requiredElement<HTMLDivElement>("scene-coverage");
const sceneCoverageCompactSummary = requiredElement<HTMLSpanElement>("scene-coverage-compact-summary");
const table = requiredElement<HTMLDivElement>("virtual-table");
const rows = requiredElement<HTMLDivElement>("virtual-rows");
const tableSummary = requiredElement<HTMLSpanElement>("table-summary");
const selectedSummary = requiredElement<HTMLSpanElement>("selected-summary");
const filterInput = requiredElement<HTMLInputElement>("filter");
const propertyInput = requiredElement<HTMLInputElement>("property");
const valueInput = requiredElement<HTMLInputElement>("value");
const proposalOutput = requiredElement<HTMLPreElement>("proposal-output");
const runtimeMetrics = requiredElement<HTMLDListElement>("runtime-metrics");
const localIfcFileInput = requiredElement<HTMLInputElement>("local-ifc-file");
const localIfcOutput = requiredElement<HTMLPreElement>("local-ifc-output");
const localIfcProjectionState = requiredElement<HTMLParagraphElement>("local-ifc-projection-state");
const importDiagnostics = requiredElement<HTMLElement>("import-diagnostics");
const localShellMetrics = requiredElement<HTMLPreElement>("local-shell-metrics");
const toolContent = requiredElement<HTMLDivElement>("tool-content");
const draftLedger = requiredElement<HTMLDivElement>("draft-ledger");
const activeSourceLabel = requiredElement<HTMLSpanElement>("active-source-label");
const inspectorTitle = requiredElement<HTMLHeadingElement>("inspector-title");
const exportDialog = requiredElement<HTMLDialogElement>("export-dialog");
const localExportSurface = requiredElement<HTMLDivElement>("local-export-surface");
const sourceDialog = requiredElement<HTMLDialogElement>("source-dialog");
const measurementsDialog = requiredElement<HTMLDialogElement>("measurements-dialog");
const coverageDialog = requiredElement<HTMLDialogElement>("coverage-dialog");
const authDialog = requiredElement<HTMLDialogElement>("auth-dialog");
const authContent = requiredElement<HTMLDivElement>("auth-content");
const authAccountButton = requiredElement<HTMLButtonElement>("auth-account");
const openIfcButton = requiredElement<HTMLButtonElement>("open-ifc");
const openIfcLabel = requiredElement<HTMLSpanElement>("open-ifc-label");
let currentLocalShellMetrics: LocalShellMetrics | null = null;
let localOpenCount = 0;
let observeAfterHeaderPick = false;
let sceneInteractionMode: SceneInteractionMode = "navigate";
let pendingAuthTool: AuthToolId = "inspect";
const compactWorkspaceMedia = window.matchMedia("(max-width: 1180px)");
const narrowWorkspaceMedia = window.matchMedia("(max-width: 700px)");
function currentWorkspaceLayout(): WorkspaceLayout {
  return narrowWorkspaceMedia.matches ? "narrow" : compactWorkspaceMedia.matches ? "compact" : "wide";
}
let workspaceLayout: WorkspaceLayout = currentWorkspaceLayout();
let workspaceShellState: WorkspaceShellState = reconcileWorkspaceForLayout(defaultWorkspaceShellState, workspaceLayout);

const scene = new FixtureScene(
  sceneContainer,
  currentFixture.elements,
  focusSingleElement,
  toggleSceneRangeSelection
);
const anonymousAuthClient = new AnonymousAuthClient();
const browserIdentityClient = new BrowserIdentityClient();
const identityBffEnabled = window.location.origin === "https://aec-workbench.0xkaz.com";
function activeAuthClient(): AuthClient {
  if (import.meta.env.DEV && window.__aecTestAuthClient !== undefined) return window.__aecTestAuthClient;
  return identityBffEnabled ? browserIdentityClient : anonymousAuthClient;
}
const authClient: AuthClient = {
  start: (returnTo) => activeAuthClient().start(returnTo),
  verify: (attempt, callback) => activeAuthClient().verify(attempt, callback),
  signOut: () => activeAuthClient().signOut()
};
const authFlow = new AuthFlow(authClient, () => renderAuthState());

async function restoreIdentitySession(): Promise<void> {
  if (!identityBffEnabled) return;
  const restored = await browserIdentityClient.restore();
  if (restored.state !== "verified") {
    if (authFlow.snapshot().identity !== null && restored.state === "refused") {
      authFlow.expire("Your Identity session ended. Sign in again to use cloud workspace access.");
    }
    return;
  }
  const parameters = new URLSearchParams(window.location.search);
  const returnTo = parameters.get("auth") === "success"
    ? parameters.get("return_to") ?? "/workspace?tool=inspect"
    : "/workspace?tool=inspect";
  authFlow.restore(restored.identity, restored.organizations, returnTo);
  if (parameters.has("auth") || parameters.has("return_to")) {
    window.history.replaceState({}, "", window.location.pathname);
  }
}

void restoreIdentitySession();
if (identityBffEnabled) {
  window.addEventListener("focus", () => void restoreIdentitySession());
}

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Required element is missing: ${id}`);
  }
  return element as T;
}

function yieldBrowserFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function setIfcLoading(loading: boolean): void {
  openIfcButton.disabled = loading;
  openIfcButton.classList.toggle("loading", loading);
  openIfcButton.setAttribute("aria-busy", String(loading));
  openIfcLabel.textContent = loading ? "Loading IFC…" : "Open IFC";
  const observeButton = document.getElementById("observe-local-ifc") as HTMLButtonElement | null;
  if (observeButton !== null) observeButton.disabled = loading;
}

function syncWorkspaceDrawerBounds(): void {
  const workspaceBounds = workspace.getBoundingClientRect();
  const sceneBounds = sceneContainer.getBoundingClientRect();
  workspace.style.setProperty("--drawer-top", `${Math.max(0, sceneBounds.top - workspaceBounds.top)}px`);
  workspace.style.setProperty("--drawer-bottom", `${Math.max(0, workspaceBounds.bottom - sceneBounds.bottom)}px`);
  // Narrow layout opens a browser/inspector drawer nearly full-width, which otherwise sits directly
  // over the bottom camera dock (Pan/Select/Frame all/Clear) since both are bottom-anchored over the
  // same scene area. Reserve the dock's own rendered height (plus its 0.75rem inset) so the narrow
  // media query below can lift the drawer's bottom edge clear of it instead of covering it.
  const dock = sceneContainer.querySelector<HTMLElement>(".scene-camera-dock");
  const dockHeight = dock === null ? 0 : dock.getBoundingClientRect().height;
  workspace.style.setProperty("--dock-clearance", dockHeight > 0 ? `${dockHeight + 12}px` : "0px");
  positionReportResizeHandle();
}

function renderWorkspaceShell(): void {
  workspace.dataset.layout = workspaceLayout;
  workspace.classList.toggle("browser-closed", !workspaceShellState.browserOpen);
  workspace.classList.toggle("inspector-closed", !workspaceShellState.inspectorOpen);
  modelBrowserPanel.setAttribute("aria-hidden", String(!workspaceShellState.browserOpen));
  inspectorPanel.setAttribute("aria-hidden", String(!workspaceShellState.inspectorOpen));
  requiredElement<HTMLButtonElement>("toggle-browser").setAttribute("aria-expanded", String(workspaceShellState.browserOpen));
  requiredElement<HTMLButtonElement>("toggle-browser").textContent = workspaceShellState.browserOpen ? "Hide browse" : "Browse";
  requestAnimationFrame(syncWorkspaceDrawerBounds);
}

function reconcileResponsiveWorkspace(): void {
  const nextLayout = currentWorkspaceLayout();
  if (nextLayout === workspaceLayout) {
    syncWorkspaceDrawerBounds();
    return;
  }
  workspaceLayout = nextLayout;
  workspaceShellState = reconcileWorkspaceForLayout(workspaceShellState, workspaceLayout);
  renderWorkspaceShell();
  renderToolPanel();
}

compactWorkspaceMedia.addEventListener("change", reconcileResponsiveWorkspace);
narrowWorkspaceMedia.addEventListener("change", reconcileResponsiveWorkspace);
new ResizeObserver(syncWorkspaceDrawerBounds).observe(scenePanel);

const utilityDialogs = [authDialog, exportDialog, sourceDialog, measurementsDialog, coverageDialog] as const;

function openDialog(dialog: HTMLDialogElement): void {
  for (const candidate of utilityDialogs) {
    if (candidate !== dialog && candidate.open) candidate.close();
  }
  if (!dialog.open) dialog.showModal();
}

function currentProjectSession(): { readonly authenticated: boolean; readonly organizationSelected: boolean; readonly projectModelOpen: boolean } {
  const auth = authFlow.snapshot();
  return {
    authenticated: auth.identity !== null,
    organizationSelected: auth.organization !== null,
    projectModelOpen: auth.status === "project-readonly"
  };
}

function currentAuthTool(): AuthToolId {
  return state.activeTool === "codes" || state.activeTool === "components" ? state.activeTool : "inspect";
}

function authStatusTitle(auth: AuthState): string {
  switch (auth.status) {
    case "anonymous-local": return "Connect a cloud workspace";
    case "starting": return "Starting sign in";
    case "callback-verifying": return "Verifying callback";
    case "authenticated-no-organization": return "Choose an organization";
    case "authenticated-no-project": return "Choose a project";
    case "project-readonly": return "Cloud context ready";
    case "expired": return "Session expired";
    case "refused": return "Sign in refused";
    case "unavailable": return "Cloud sign in unavailable";
  }
}

function appendAuthButton(
  host: HTMLElement,
  label: string,
  action: () => void,
  className = ""
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.className = className;
  button.addEventListener("click", action);
  host.append(button);
  return button;
}

function appendAuthContext(host: HTMLElement, auth: AuthState): void {
  if (auth.identity === null) return;
  const context = document.createElement("dl");
  context.className = "auth-context";
  for (const [label, value] of [
    ["Account", auth.identity.displayName],
    ["Organization", auth.organization?.name ?? "Not selected"],
    ["Project", auth.project?.name ?? "Not selected"],
    ["Access", auth.project === null ? "No project access" : "Read-only"]
  ]) {
    const term = document.createElement("dt");
    term.textContent = label;
    const description = document.createElement("dd");
    description.textContent = value;
    context.append(term, description);
  }
  host.append(context);
}

function finishAuthReturn(): void {
  const auth = authFlow.snapshot();
  if (auth.status !== "project-readonly") return;
  const targetTool = auth.returnTo?.tool ?? pendingAuthTool;
  state.activeTool = targetTool;
  workspaceShellState = openInspectorForTool(workspaceShellState, workspaceLayout);
  authDialog.close();
  renderWorkspaceShell();
  renderToolPanel();
}

async function startAuth(): Promise<void> {
  await authFlow.signIn(authReturnTo(pendingAuthTool));
  renderAuthState();
}

function renderAuthSurface(): void {
  const auth = authFlow.snapshot();
  authContent.replaceChildren();
  authDialog.querySelector<HTMLHeadingElement>("#auth-dialog-title")!.textContent = auth.status === "project-readonly" ? "Cloud account" : "Sign in";
  const card = document.createElement("section");
  card.className = "auth-flow-card";
  card.dataset.state = auth.status;
  const status = document.createElement("span");
  status.className = "auth-state-badge";
  status.textContent = auth.status;
  const title = document.createElement("h3");
  title.textContent = authStatusTitle(auth);
  const message = document.createElement("p");
  message.textContent = auth.message;
  card.append(status, title, message);
  appendAuthContext(card, auth);
  const actions = document.createElement("div");
  actions.className = "auth-actions";

  if (auth.status === "anonymous-local" || auth.status === "expired" || auth.status === "refused" || auth.status === "unavailable") {
    appendAuthButton(actions, auth.status === "anonymous-local" ? "Sign in" : "Try sign in again", () => void startAuth(), "primary-action");
    if (auth.status !== "anonymous-local") appendAuthButton(actions, "Continue locally", () => authDialog.close());
  } else if (auth.status === "starting" || auth.status === "callback-verifying") {
    const progress = document.createElement("span");
    progress.className = "auth-progress";
    progress.textContent = auth.status === "starting" ? "Preparing secure handoff…" : "Checking callback evidence…";
    actions.append(progress);
    appendAuthButton(actions, "Cancel", () => authFlow.cancel());
  } else if (auth.status === "authenticated-no-organization") {
    for (const organization of auth.organizations) {
      appendAuthButton(actions, organization.name, () => authFlow.selectOrganization(organization.id), "primary-action");
    }
    if (auth.organizations.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "No organization is available for this identity.";
      actions.append(empty);
    }
    appendAuthButton(actions, "Account settings", () => window.open(identityConsoleUrl(), "_blank", "noopener,noreferrer"));
    appendAuthButton(actions, "Sign out", () => void authFlow.signOut());
  } else if (auth.status === "authenticated-no-project") {
    for (const project of auth.organization?.projects ?? []) {
      appendAuthButton(actions, project.name, () => {
        authFlow.selectProject(project.id);
        finishAuthReturn();
      }, "primary-action");
    }
    appendAuthButton(actions, "Account settings", () => window.open(identityConsoleUrl(), "_blank", "noopener,noreferrer"));
    appendAuthButton(actions, "Sign out", () => void authFlow.signOut());
  } else {
    appendAuthButton(actions, `Continue to ${auth.returnTo?.tool === "components" ? "Components" : auth.returnTo?.tool === "codes" ? "Codes" : "Inspect"}`, finishAuthReturn, "primary-action");
    for (const organization of auth.organizations.filter((candidate) => candidate.id !== auth.organization?.id)) {
      appendAuthButton(actions, `Switch to ${organization.name}`, () => authFlow.selectOrganization(organization.id));
    }
    appendAuthButton(actions, "Account settings", () => window.open(identityConsoleUrl(), "_blank", "noopener,noreferrer"));
    appendAuthButton(actions, "Sign out", () => void authFlow.signOut());
  }
  card.append(actions);
  const boundary = document.createElement("p");
  boundary.className = "auth-boundary-note";
  boundary.textContent = "Local files and pending changes stay on this device. Attaching them to a cloud project is a separate confirmed action.";
  authContent.append(card, boundary);
}

function renderAuthState(): void {
  const auth = authFlow.snapshot();
  authAccountButton.dataset.state = auth.status;
  authAccountButton.textContent = authAccountButtonLabel(auth);
  authAccountButton.setAttribute("aria-label", authAccountButtonAriaLabel(auth));
  if (authDialog.open) renderAuthSurface();
  renderToolPanel();
}

function openAuthForTool(tool: AuthToolId): void {
  pendingAuthTool = tool;
  renderAuthSurface();
  openDialog(authDialog);
}

requiredElement<HTMLButtonElement>("open-source-details").addEventListener("click", () => openDialog(sourceDialog));
requiredElement<HTMLButtonElement>("open-export").addEventListener("click", () => openDialog(exportDialog));
requiredElement<HTMLButtonElement>("open-measurements").addEventListener("click", () => openDialog(measurementsDialog));
requiredElement<HTMLButtonElement>("coverage-trigger").addEventListener("click", () => openDialog(coverageDialog));
authAccountButton.addEventListener("click", () => openAuthForTool(currentAuthTool()));
for (const button of document.querySelectorAll<HTMLButtonElement>("[data-close-dialog]")) {
  button.addEventListener("click", () => {
    const dialogId = button.dataset.closeDialog;
    if (dialogId !== undefined) requiredElement<HTMLDialogElement>(dialogId).close();
  });
}
for (const dialog of utilityDialogs) {
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
}

function resetTableFilters(): void {
  state.filter = "";
  state.typeFilter = null;
  state.outcomeFilter = null;
  filterInput.value = "";
  table.scrollTop = 0;
}

function focusHierarchy(node: InspectionHierarchyNode | LocalValidationTypeSummary): void {
  const nodeId = "id" in node ? node.id : `type:${node.type}`;
  const label = "label" in node ? node.label : `Type · ${node.type}`;
  state.hierarchyFocus = {
    nodeId,
    label,
    elementIds: new Set(node.elementIds)
  };
  expandedHierarchyIds.add(currentHierarchy.id);
  for (const scope of currentHierarchy.children) {
    if (scope.id === nodeId || scope.children.some((child) => child.id === nodeId)) expandedHierarchyIds.add(scope.id);
  }
  for (const scope of currentHierarchy.children) {
    for (const type of scope.children) expandedHierarchyIds.delete(type.id);
  }
  expandedHierarchyIds.add(nodeId);
  currentValidationFocus = null;
  state.bulkPropertyAction = "";
  resetTableFilters();
  renderHierarchy();
  render();
}

function hierarchyButton(node: InspectionHierarchyNode, depth: number): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `hierarchy-node hierarchy-${node.kind}`;
  button.style.setProperty("--tree-depth", String(depth));
  button.setAttribute("role", "treeitem");
  button.setAttribute("aria-level", String(depth + 1));
  const active = state.hierarchyFocus?.nodeId === node.id;
  const expanded = expandedHierarchyIds.has(node.id);
  button.setAttribute("aria-pressed", String(active));
  button.setAttribute("aria-expanded", String(expanded));
  button.classList.toggle("active", active);
  const disclosure = document.createElement("span");
  disclosure.className = "hierarchy-disclosure";
  disclosure.setAttribute("aria-hidden", "true");
  disclosure.textContent = expanded ? "▾" : "▸";
  const label = document.createElement("span");
  label.className = "hierarchy-label";
  label.textContent = node.label;
  button.title = `${node.records} records · ${node.renderable} shown in 3D · ${node.editable} editable`;
  button.append(disclosure, label);
  button.addEventListener("click", () => {
    if (node.kind !== "type") {
      if (expanded) {
        expandedHierarchyIds.delete(node.id);
        if (node.kind === "dataset" || node.children.some((child) => child.id === state.hierarchyFocus?.nodeId)) {
          state.hierarchyFocus = null;
        }
      } else {
        expandedHierarchyIds.add(node.id);
      }
      renderHierarchy();
      render();
      return;
    }
    if (active && expanded) {
      state.hierarchyFocus = null;
      expandedHierarchyIds.delete(node.id);
      renderHierarchy();
      render();
      return;
    }
    focusHierarchy(node);
  });
  return button;
}

function appendHierarchyElements(depth: number, label = "Elements"): void {
  const elementGroupHeading = document.createElement("div");
  elementGroupHeading.className = "hierarchy-element-heading";
  elementGroupHeading.style.setProperty("--tree-depth", String(depth));
  elementGroupHeading.setAttribute("role", "presentation");
  const elementGroupLabel = document.createElement("strong");
  elementGroupLabel.textContent = label;
  elementGroupHeading.append(elementGroupLabel);
  table.style.setProperty("--tree-depth", String(depth));
  modelHierarchy.append(elementGroupHeading, table);
}

function renderHierarchy(): void {
  modelHierarchy.replaceChildren();
  modelHierarchy.append(hierarchyButton(currentHierarchy, 0));
  if (expandedHierarchyIds.has(currentHierarchy.id)) {
    const hierarchyFocusIsVisible = currentHierarchy.children.some((scope) =>
      scope.id === state.hierarchyFocus?.nodeId || scope.children.some((type) => type.id === state.hierarchyFocus?.nodeId)
    );
    if (state.filter.trim() !== "" && state.hierarchyFocus === null) appendHierarchyElements(1, "Search results");
    if (state.hierarchyFocus !== null && !hierarchyFocusIsVisible) appendHierarchyElements(1, "Focused results");
    for (const scope of currentHierarchy.children) {
      modelHierarchy.append(hierarchyButton(scope, 1));
      if (!expandedHierarchyIds.has(scope.id)) continue;
      for (const type of scope.children) {
        modelHierarchy.append(hierarchyButton(type, 2));
        if (state.hierarchyFocus?.nodeId === type.id && expandedHierarchyIds.has(type.id)) appendHierarchyElements(3);
      }
    }
  }
}

function normalizePrimarySelection(): void {
  if (state.primarySelectedId !== null && state.selectedIds.has(state.primarySelectedId)) return;
  state.primarySelectedId = state.selectedIds.values().next().value ?? null;
}

function renderSelectionChange(): void {
  state.bulkPropertyAction = "";
  if (state.outcomeFilter !== null) {
    render();
    return;
  }
  normalizePrimarySelection();
  for (const row of rows.querySelectorAll<HTMLButtonElement>(".element-row")) {
    const id = row.dataset.elementId;
    if (id === undefined) continue;
    const element = currentDatasetIndex.elementsById.get(id);
    if (element === undefined) continue;
    const selected = state.selectedIds.has(id);
    const outcome = outcomeForElement(element, state.selectedIds, state.request);
    row.classList.toggle("selected", selected);
    for (const candidate of OUTCOMES) row.classList.toggle(`outcome-${candidate}`, candidate === outcome);
    row.setAttribute("aria-selected", String(selected));
    const outcomeCell = row.lastElementChild;
    if (outcomeCell !== null) outcomeCell.textContent = outcome;
  }
  scene.setSelection(state.selectedIds);
  selectedSummary.textContent = `${state.selectedIds.size} selected`;
  requiredElement<HTMLButtonElement>("clear-scene-selection").disabled = state.selectedIds.size === 0;
  renderToolPanel();
}

function toggleSelection(id: string, additive = false): void {
  const selectionStartedAt = performance.now();
  if (additive) {
    if (state.selectedIds.has(id)) state.selectedIds.delete(id);
    else state.selectedIds.add(id);
  } else {
    state.selectedIds.clear();
    state.selectedIds.add(id);
  }
  state.primarySelectedId = state.selectedIds.has(id) ? id : null;
  state.selectionAnchorId = id;
  renderSelectionChange();
  runtimeMeter.recordSelection(selectionStartedAt);
  renderRuntimeMetrics();
}

function selectTableElement(id: string, event: MouseEvent): void {
  if (event.shiftKey && state.selectionAnchorId !== null) {
    const anchor = currentVisibleElements.findIndex((element) => element.id === state.selectionAnchorId);
    const target = currentVisibleElements.findIndex((element) => element.id === id);
    if (anchor >= 0 && target >= 0) {
      const selectionStartedAt = performance.now();
      if (!event.metaKey && !event.ctrlKey) state.selectedIds.clear();
      const start = Math.min(anchor, target);
      const end = Math.max(anchor, target);
      for (let index = start; index <= end; index += 1) state.selectedIds.add(currentVisibleElements[index].id);
      state.primarySelectedId = id;
      renderSelectionChange();
      runtimeMeter.recordSelection(selectionStartedAt);
      renderRuntimeMetrics();
      return;
    }
  }
  toggleSelection(id, true);
}

function focusSingleElement(id: string): void {
  const selectionStartedAt = performance.now();
  state.selectedIds.clear();
  state.selectedIds.add(id);
  state.primarySelectedId = id;
  state.selectionAnchorId = id;
  renderSelectionChange();
  scene.focusElements(new Set([id]));
  syncRestoreViewButton();
  runtimeMeter.recordSelection(selectionStartedAt);
  renderRuntimeMetrics();
}

function syncRestoreViewButton(): void {
  requiredElement<HTMLButtonElement>("restore-view").disabled = !scene.viewState().canRestoreView;
}

function selectVisibleElements(): void {
  const selectionStartedAt = performance.now();
  for (const element of currentVisibleElements) {
    state.selectedIds.add(element.id);
  }
  state.primarySelectedId = currentVisibleElements[0]?.id ?? state.primarySelectedId;
  state.selectionAnchorId = state.primarySelectedId;
  renderSelectionChange();
  runtimeMeter.recordSelection(selectionStartedAt);
  renderRuntimeMetrics();
}

function toggleSceneRangeSelection(ids: ReadonlySet<string>): void {
  const selectionStartedAt = performance.now();
  for (const id of ids) {
    if (state.selectedIds.has(id)) state.selectedIds.delete(id);
    else state.selectedIds.add(id);
  }
  normalizePrimarySelection();
  state.selectionAnchorId = state.primarySelectedId;
  renderSelectionChange();
  runtimeMeter.recordSelection(selectionStartedAt);
  renderRuntimeMetrics();
}

function setSceneInteractionMode(mode: SceneInteractionMode): void {
  sceneInteractionMode = mode;
  scene.setInteractionMode(mode);
  const navigate = requiredElement<HTMLButtonElement>("navigate-mode");
  const pan = requiredElement<HTMLButtonElement>("pan-mode");
  const select = requiredElement<HTMLButtonElement>("select-mode");
  navigate.classList.toggle("active", mode === "navigate");
  pan.classList.toggle("active", mode === "pan");
  select.classList.toggle("active", mode === "select");
  navigate.setAttribute("aria-pressed", String(mode === "navigate"));
  pan.setAttribute("aria-pressed", String(mode === "pan"));
  select.setAttribute("aria-pressed", String(mode === "select"));
}

function renderRows(): void {
  const elements = currentVisibleElements;
  const firstRow = Math.max(0, Math.floor(table.scrollTop / rowHeight) - rowOverscan);
  const visibleRowCount = Math.ceil(table.clientHeight / rowHeight) + rowOverscan * 2;
  const lastRow = Math.min(elements.length, firstRow + visibleRowCount);
  rows.style.height = `${elements.length * rowHeight}px`;
  rows.replaceChildren();

  for (let index = firstRow; index < lastRow; index += 1) {
    const element = elements[index];
    const outcome = outcomeForElement(element, state.selectedIds, state.request);
    const row = document.createElement("button");
    row.type = "button";
    row.className = `element-row outcome-${outcome}`;
    row.classList.toggle("selected", state.selectedIds.has(element.id));
    row.style.transform = `translateY(${index * rowHeight}px)`;
    row.setAttribute("role", "treeitem");
    row.setAttribute("aria-level", "4");
    row.setAttribute("aria-selected", String(state.selectedIds.has(element.id)));
    row.dataset.elementType = element.type;
    row.dataset.elementId = element.id;
    row.dataset.editable = String(element.editable && element.unsupportedReason === undefined);

    const primary = document.createElement("span");
    const label = document.createElement("strong");
    label.textContent = element.label;
    const metadata = document.createElement("small");
    metadata.textContent = `${element.type === "" ? "(missing type)" : element.type} · ${element.id}`;
    primary.append(label, metadata);
    const classification = document.createElement("span");
    classification.textContent = element.classification === null ? "Unmapped" : element.classification.code;
    const outcomeCell = document.createElement("span");
    outcomeCell.textContent = outcome;
    row.append(primary, classification, outcomeCell);
    row.addEventListener("click", (event) => selectTableElement(element.id, event));
    row.addEventListener("dblclick", () => focusSingleElement(element.id));
    rows.append(row);
  }

  tableSummary.textContent = `${elements.length} visible / ${currentFixture.elements.length} total`;
}

function displayValue(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value === "" ? "(empty string)" : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function addDefinition(list: HTMLDListElement, label: string, value: string): void {
  const term = document.createElement("dt");
  term.textContent = label;
  const description = document.createElement("dd");
  description.textContent = value;
  list.append(term, description);
}

function renderInspectTool(): void {
  const selectedElements = selectedFixtureElements(currentDatasetIndex, state.selectedIds);
  const inspection = inspectElements(selectedElements, 40);
  const heading = document.createElement("div");
  heading.className = "tool-heading";
  const title = document.createElement("div");
  title.innerHTML = `<p>Selection attributes, properties, relations, and retained evidence.</p>`;
  const badge = document.createElement("span");
  badge.textContent = `${inspection.selectedCount} selected`;
  heading.append(title, badge);
  toolContent.append(heading);

  if (inspection.selectedCount === 0) {
    const empty = document.createElement("p");
    empty.className = "tool-empty";
    empty.textContent = "Select one or more records in the scene or table to inspect shared and mixed values.";
    toolContent.append(empty);
  } else {
    const census = document.createElement("dl");
    census.className = "inspection-census";
    addDefinition(census, "Types", Object.entries(inspection.typeCounts).map(([type, count]) => `${type} ${count}`).join(" · "));
    addDefinition(census, "Properties", `${inspection.visiblePropertyCount}/${inspection.propertyCount} materialized`);
    addDefinition(census, "Unmapped", String(inspection.unmappedCount));
    addDefinition(census, "Unsupported", String(inspection.unsupportedCount));
    addDefinition(census, "Unknown retained", String(inspection.unknownRecordCount));
    toolContent.append(census);

    const propertyGrid = document.createElement("div");
    propertyGrid.className = "inspection-grid";
    const shared = document.createElement("section");
    shared.innerHTML = "<h3>Shared properties</h3>";
    const sharedList = document.createElement("dl");
    sharedList.className = "property-list";
    for (const [property, value] of Object.entries(inspection.sharedProperties)) {
      addDefinition(sharedList, property, displayValue(value));
    }
    if (sharedList.childElementCount === 0) sharedList.textContent = "No identical property values across the selection.";
    shared.append(sharedList);

    const mixed = document.createElement("section");
    mixed.innerHTML = "<h3>Mixed or missing</h3>";
    const mixedList = document.createElement("dl");
    mixedList.className = "property-list";
    for (const [property, values] of Object.entries(inspection.mixedProperties)) {
      addDefinition(mixedList, property, values.map((entry) => `${displayValue(entry.value)} × ${entry.count}`).join(" · "));
    }
    if (mixedList.childElementCount === 0) mixedList.textContent = "No mixed values in this selection.";
    mixed.append(mixedList);
    propertyGrid.append(shared, mixed);
    toolContent.append(propertyGrid);
    if (inspection.visiblePropertyCount < inspection.propertyCount) {
      const bounded = document.createElement("p");
      bounded.className = "tool-note";
      bounded.textContent = `${inspection.propertyCount - inspection.visiblePropertyCount} additional properties remain retained outside this bounded Inspect view.`;
      toolContent.append(bounded);
    }

    const edit = document.createElement("section");
    edit.className = "inspection-edit bulk-property-editor";
    const editableOptions = editablePropertyOptions(selectedElements);
    if (editableOptions.editableCount === 0) {
      edit.innerHTML = `
        <div class="bulk-property-heading"><div><h3>Edit selected properties</h3><p>None of the ${inspection.selectedCount} selected records are editable or writer-supported yet. Select at least one editable element to propose a change.</p></div></div>`;
      toolContent.append(edit);
    } else if (editableOptions.propertyNames.length === 0) {
      edit.innerHTML = `
        <div class="bulk-property-heading"><div><h3>Edit selected properties</h3><p>The ${editableOptions.editableCount} editable record(s) in this selection have no properties to edit.</p></div></div>`;
      toolContent.append(edit);
    } else {
      const scopeNote = editableOptions.editableCount === inspection.selectedCount
        ? "Changes apply only to the current selection. Unselected elements and the source IFC remain unchanged until export or save."
        : `Only the ${editableOptions.editableCount} of ${inspection.selectedCount} selected records that are editable can be changed here; the rest stay untouched.`;
      edit.innerHTML = `
        <div class="bulk-property-heading"><div><h3>Edit selected properties</h3><p>${scopeNote}</p></div><span>${editableOptions.editableCount} editable</span></div>
        <div class="bulk-property-controls">
          <label>Property <select id="inspect-draft-property" aria-label="Bulk property"></select></label>
          <label>Action <select id="inspect-draft-operation" aria-label="Bulk property action"><option value="replace">Replace all</option><option value="clear">Clear all</option><option value="remove">Remove</option></select></label>
          <label id="inspect-draft-value-label">Value <input id="inspect-draft-value" aria-label="Bulk property value" /></label>
          <button id="inspect-stage-draft" type="button">Add for ${editableOptions.editableCount} selected</button>
        </div>
        <p id="bulk-property-status" class="tool-note" aria-live="polite"></p>`;
      toolContent.append(edit);
      const inspectProperty = requiredElement<HTMLSelectElement>("inspect-draft-property");
      const inspectOperation = requiredElement<HTMLSelectElement>("inspect-draft-operation");
      const inspectValueLabel = requiredElement<HTMLLabelElement>("inspect-draft-value-label");
      const inspectValue = requiredElement<HTMLInputElement>("inspect-draft-value");
      // Only real property names of the editable subset are ever offered -- see
      // editablePropertyOptions' doc comment for why free text (or a merely-suggested datalist)
      // let a synthetic, always-refused name through before this (kaz, 2026-08-12).
      for (const property of editableOptions.propertyNames) {
        const option = document.createElement("option");
        option.value = property;
        option.textContent = property;
        inspectProperty.append(option);
      }
      const samePropertyAsBefore = editableOptions.propertyNames.includes(state.request.property);
      if (samePropertyAsBefore) inspectProperty.value = state.request.property;
      // Prefill Value with the editable subset's actual current value whenever the user actively
      // *picks* a property, instead of leaving behind whatever was last typed for a different
      // property (kaz, 2026-08-12: "Propertyを選んだ時点で、現在のバリューを読み込んだ方がいい").
      // Only an unambiguous shared primitive is prefilled -- see currentEditablePropertyValue's doc
      // comment. This only runs on the explicit `change` event, not on every render: a successful
      // "Add" re-renders this same form for the same property, and re-running the prefill there would
      // discard the value the user just typed and submitted right back to its pre-edit original (kaz,
      // 2026-08-12, flagged from real use immediately after the first fix). On a render where the
      // property shown is unchanged from before, the Value field keeps `state.request.value` (what was
      // last typed/submitted) instead.
      const applyCurrentValuePrefill = (): void => {
        const current = currentEditablePropertyValue(selectedElements, inspectProperty.value);
        inspectValue.value = current.state === "shared" ? current.text : "";
        inspectValue.placeholder = current.state === "mixed"
          ? "Mixed current values"
          : current.state === "non-primitive"
            ? "Current value is not a simple text/number/boolean"
            : current.state === "missing"
              ? "Not currently set on every editable element"
              : "";
      };
      if (samePropertyAsBefore) inspectValue.value = state.request.value;
      else applyCurrentValuePrefill();
      requiredElement<HTMLParagraphElement>("bulk-property-status").textContent = state.bulkPropertyAction;
      inspectOperation.addEventListener("change", () => {
        inspectValueLabel.hidden = inspectOperation.value !== "replace";
      });
      inspectProperty.addEventListener("change", applyCurrentValuePrefill);
      requiredElement<HTMLButtonElement>("inspect-stage-draft").addEventListener("click", () => {
        stageBulkPropertyDraft(inspectProperty.value, inspectOperation.value as DraftOperation, inspectValue.value);
      });
    }
  }

}

function stageBulkPropertyDraft(property: string, operation: DraftOperation, value: string): void {
  state.request.property = property;
  if (operation === "replace") state.request.value = value;
  propertyInput.value = property;
  if (operation === "replace") valueInput.value = value;
  try {
    const result = stageBulkPropertyChange(currentFixture, state.draft, state.selectedIds, property, operation, value);
    state.draft = result.ledger;
    resetLocalExport();
    state.bulkPropertyAction = `${result.selected} selected · ${result.staged} staged · ${result.alreadyMissing} already missing · ${result.refused} refused`;
    proposalOutput.textContent = JSON.stringify(state.draft, null, 2);
    proposalOutput.dataset.state = "ready";
    render();
    // render() alone does not touch #draft-ledger (the "Pending changes"/"BIM-Hub commit review"
    // section) -- without this, a change staged here never showed up in the commit payload preview
    // or re-enabled "Send to BIM-Hub", even though it really was staged (kaz, 2026-08-12, found right
    // after a real successful save: staging a further edit left the button looking stuck disabled).
    renderDraftLedger();
  } catch (error) {
    state.bulkPropertyAction = error instanceof Error ? error.message : "Bulk property staging failed.";
    proposalOutput.textContent = state.bulkPropertyAction;
    proposalOutput.dataset.state = "error";
    renderToolPanel();
  }
}

function stageTrackedDraft(property: string, value: string, source: DraftSource): void {
  state.request.property = property;
  state.request.value = value;
  propertyInput.value = property;
  valueInput.value = value;
  try {
    // A mixed selection stages every editable element and reports the rest as refused, rather than
    // discarding the whole batch over one read-only element in it (kaz, 2026-08-11 -- see
    // stageDraftChange's own doc comment).
    const result = stageDraftChange(currentFixture, state.draft, state.selectedIds, property, value, source);
    state.draft = result.ledger;
    resetLocalExport();
    const summary = `${result.selected} selected · ${result.staged} staged · ${result.refused} refused`;
    proposalOutput.textContent = `${summary}\n\n${JSON.stringify(state.draft, null, 2)}`;
    proposalOutput.dataset.state = "ready";
    render();
    // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
    renderDraftLedger();
  } catch (error) {
    proposalOutput.textContent = error instanceof Error ? error.message : "Pending change could not be added.";
    proposalOutput.dataset.state = "error";
  }
}

function renderCodesTool(): void {
  const access = sharedLibraryAccess("codes", currentProjectSession());
  const actionLabel = access.state === "authentication-required"
    ? "Sign in for Codes"
    : access.state === "organization-required"
      ? "Choose organization"
      : access.state === "project-model-required"
      ? "Choose cloud project"
      : "Cloud account";
  toolContent.innerHTML = `
    <div class="tool-heading"><div><p>Classification libraries belong to an authenticated project context.</p></div><span>${access.state === "available" ? "read-only context" : access.state === "project-model-required" ? "project required" : access.state === "organization-required" ? "organization required" : "sign-in required"}</span></div>
    <section class="auth-gate">
      <h3>${access.reason}</h3>
      <p>${access.state === "available" ? "The read-only project context is ready. Live versioned classification library access remains a separate contract." : "Choose an authenticated read-only project context to prepare versioned classification library access."}</p>
      <button id="codes-auth-action" type="button">${actionLabel}</button>
      <small>Existing classification properties remain visible in Inspect. No local catalog, relation, or source IFC is mutated by authentication.</small>
    </section>`;
  requiredElement<HTMLButtonElement>("codes-auth-action").addEventListener("click", () => openAuthForTool("codes"));
  toolContent.append(renderClassificationStaging());
}

function renderClassificationStaging(): HTMLElement {
  const section = document.createElement("section");
  section.className = "classification-staging";
  const eligible = selectedFixtureElements(currentDatasetIndex, state.selectedIds).filter(
    (element) => element.editable && element.unsupportedReason === undefined
  );
  section.innerHTML = `
    <h3>Local classification proposal</h3>
    <p>Maps selected elements to a classification reference as a local, session-only <code>workbench-local-classification-proposal/1</code>. This does not claim catalog definition authority and is not applied by the current writer.</p>
    <div class="library-action">
      <label>System <input class="classification-system" value="Uniclass 2015" aria-label="Classification system" /></label>
      <label>Code <input class="classification-code" aria-label="Classification code" /></label>
      <label>Label <input class="classification-label" aria-label="Classification label" /></label>
      <button class="stage-classification" type="button">Stage for ${eligible.length} selected</button>
    </div>
    <p class="classification-staging-status tool-note" aria-live="polite"></p>`;
  const systemInput = section.querySelector<HTMLInputElement>(".classification-system")!;
  const codeInput = section.querySelector<HTMLInputElement>(".classification-code")!;
  const labelInput = section.querySelector<HTMLInputElement>(".classification-label")!;
  const statusText = section.querySelector<HTMLParagraphElement>(".classification-staging-status")!;
  statusText.textContent = state.classificationAction;
  const stageButton = section.querySelector<HTMLButtonElement>(".stage-classification")!;
  stageButton.disabled = eligible.length === 0;
  stageButton.addEventListener("click", () => {
    const system = systemInput.value.trim();
    const code = codeInput.value.trim();
    const label = labelInput.value.trim();
    if (system === "" || code === "" || label === "") {
      state.classificationAction = "System, code, and label are all required to stage a classification mapping.";
      renderToolPanel();
      return;
    }
    for (const element of eligible) classificationStagedAfter.set(element.id, { system, code, label });
    state.classificationAction = `Staged ${eligible.length} classification mapping(s) for local proposal review.`;
    render();
    // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
    renderDraftLedger();
  });

  if (classificationStagedAfter.size > 0) {
    const list = document.createElement("div");
    list.className = "draft-list";
    for (const [elementId, after] of [...classificationStagedAfter.entries()].sort(([left], [right]) => left.localeCompare(right))) {
      const row = document.createElement("div");
      row.className = "draft-row";
      const values = document.createElement("div");
      const label = document.createElement("strong");
      label.textContent = elementId;
      const diff = document.createElement("span");
      diff.textContent = `${after.system} · ${after.code} · ${after.label}`;
      values.append(label, diff);
      const remove = document.createElement("button");
      remove.type = "button";
      remove.textContent = "Remove";
      remove.addEventListener("click", () => {
        classificationStagedAfter.delete(elementId);
        render();
        // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
        renderDraftLedger();
      });
      row.append(values, remove);
      list.append(row);
    }
    section.append(list);
  }
  return section;
}

function renderComponentsTool(): void {
  const access = sharedLibraryAccess("components", currentProjectSession());
  const actionLabel = access.state === "authentication-required"
    ? "Sign in for Components"
    : access.state === "organization-required"
      ? "Choose organization"
      : access.state === "project-model-required"
      ? "Choose cloud project"
      : "Cloud account";
  toolContent.innerHTML = `
    <div class="tool-heading"><div><p>Shared component libraries belong to an authenticated project context.</p></div><span>${access.state === "available" ? "read-only context" : access.state === "project-model-required" ? "project required" : access.state === "organization-required" ? "organization required" : "sign-in required"}</span></div>
    <section class="auth-gate">
      <h3>${access.reason}</h3>
      <p>${access.state === "available" ? "The read-only project context is ready. Live versioned component library access remains a separate contract." : "Choose an authenticated read-only project context to prepare versioned component library access."}</p>
      <button id="components-auth-action" type="button">${actionLabel}</button>
      <small>Existing component, type, or specification properties remain visible in Inspect. Authentication does not replace geometry or attach the local source.</small>
    </section>`;
  requiredElement<HTMLButtonElement>("components-auth-action").addEventListener("click", () => openAuthForTool("components"));
}

function renderImportDiagnostics(): void {
  const summary = currentQualitySummary;
  const coverage = currentProjectionCoverage;
  importDiagnostics.replaceChildren();
  const heading = document.createElement("div");
  heading.className = "import-diagnostics-heading";
  const title = document.createElement("div");
  const titleText = document.createElement("h3");
  titleText.textContent = "Import diagnostics";
  const note = document.createElement("p");
  note.textContent = "Producer geometry and EXPRESS evidence. These records are not project checks or collaboration issues.";
  title.append(titleText, note);
  const total = document.createElement("span");
  total.textContent = currentQuality === null ? "No local IFC" : `${summary.total} records`;
  heading.append(title, total);
  importDiagnostics.append(heading);

  if (currentQuality === null) {
    const empty = document.createElement("p");
    empty.className = "tool-note";
    empty.textContent = "Open a local IFC to inspect producer diagnostics.";
    importDiagnostics.append(empty);
    return;
  }

  const severityFilters = document.createElement("div");
  severityFilters.className = "quality-filters";
  const severityOptions: readonly (QualitySeverity | "all")[] = ["all", "error", "warning", "info", "unknown"];
  for (const severity of severityOptions) {
    const button = document.createElement("button");
    button.type = "button";
    button.classList.toggle("active", state.qualitySeverity === severity);
    const count = severity === "all" ? summary.total : summary.severityCounts[severity];
    button.textContent = `${severity} ${count}`;
    button.addEventListener("click", () => {
      state.qualitySeverity = severity;
      renderImportDiagnostics();
    });
    severityFilters.append(button);
  }
  importDiagnostics.append(severityFilters);

  const census = document.createElement("dl");
  census.className = "inspection-census quality-census";
  addDefinition(census, "Diagnostic records", String(summary.total));
  addDefinition(census, "Linked records", String(summary.linkedCount));
  addDefinition(census, "Unlinked records", String(summary.unlinkedCount));
  addDefinition(census, "Diagnostic rules", String(summary.ruleCounts.length));
  importDiagnostics.append(census);

  const skippedEvidence = document.createElement("section");
  skippedEvidence.className = "quality-skipped-evidence";
  const skippedHeading = document.createElement("h3");
  skippedHeading.textContent = `Producer skipped evidence · ${coverage.producerSkippedRecords}`;
  const skippedNote = document.createElement("p");
  skippedNote.textContent =
    coverage.producerSkippedRecords === 0
      ? "No producer skipped outcomes are present in this inspection dataset."
      : `${coverage.propertySetSkippedRecords} property-set evidence records are non-geometric; ${coverage.unsupportedGeometrySkippedRecords} outcomes explicitly report unsupported geometry.`;
  skippedEvidence.append(skippedHeading, skippedNote);
  const skippedTypes = document.createElement("div");
  skippedTypes.className = "quality-rules";
  for (const item of coverage.skippedTypes) {
    const badge = document.createElement("span");
    badge.textContent = `${item.value} · ${item.count}`;
    skippedTypes.append(badge);
  }
  const skippedReasons = document.createElement("div");
  skippedReasons.className = "quality-rules";
  for (const item of coverage.skippedReasons) {
    const badge = document.createElement("span");
    badge.textContent = `${item.value} · ${item.count}`;
    skippedReasons.append(badge);
  }
  skippedEvidence.append(skippedTypes, skippedReasons);
  importDiagnostics.append(skippedEvidence);

  const rules = document.createElement("div");
  rules.className = "quality-rules";
  for (const item of summary.ruleCounts) {
    const badge = document.createElement("span");
    badge.textContent = `${item.rule} · ${item.count}`;
    rules.append(badge);
  }
  importDiagnostics.append(rules);

  const records = document.createElement("div");
  records.className = "quality-issues";
  const visibleRecords = qualityIssuePage(summary, state.qualitySeverity, 200);
  for (const issue of visibleRecords) {
    const row = document.createElement("article");
    row.className = `quality-issue severity-${issue.severity}`;
    const evidence = document.createElement("div");
    const recordHeading = document.createElement("strong");
    recordHeading.textContent = `${issue.severity} · ${issue.rule}`;
    const message = document.createElement("span");
    message.textContent = issue.message;
    const identity = document.createElement("small");
    identity.textContent = issue.linkage === "linked"
      ? `${issue.matchedElementIds.length} exact GlobalId match`
      : `${issue.globalIds.length} source GlobalIds · no projected identity match`;
    evidence.append(recordHeading, message, identity);
    const select = document.createElement("button");
    select.type = "button";
    select.textContent = issue.linkage === "linked" ? "Select elements" : "Unlinked";
    select.disabled = issue.linkage !== "linked";
    select.addEventListener("click", () => {
      const selectionStartedAt = performance.now();
      state.selectedIds.clear();
      for (const id of issue.matchedElementIds) state.selectedIds.add(id);
      render();
      runtimeMeter.recordSelection(selectionStartedAt);
      renderRuntimeMetrics();
    });
    row.append(evidence, select);
    records.append(row);
  }
  importDiagnostics.append(records);
}

function renderQualityTool(): void {
  toolContent.innerHTML = `
    <div class="tool-heading"><div><p>Local data checks for missing or invalid model information.</p></div><span>candidate · partial</span></div>`;

  if (currentValidationOverview === null) {
    currentValidationOverview = summarizeLocalValidation(currentFixture.elements);
  }
  const overview = document.createElement("section");
  overview.className = "quality-overview";
  const overviewHeading = document.createElement("div");
  overviewHeading.className = "overview-heading";
  const overviewTitle = document.createElement("div");
  const overviewTitleText = document.createElement("h3");
  overviewTitleText.textContent = "Data quality overview";
  const overviewNote = document.createElement("p");
  overviewNote.textContent = `${currentValidationOverview.recordCount} records · ${currentValidationOverview.assessmentCount} partial assessments · candidate rules only`;
  overviewTitle.append(overviewTitleText, overviewNote);
  const clearFocus = document.createElement("button");
  clearFocus.type = "button";
  clearFocus.textContent = "Clear quality focus";
  clearFocus.disabled = currentValidationFocus === null;
  clearFocus.addEventListener("click", () => {
    currentValidationFocus = null;
    state.hierarchyFocus = null;
    state.qualityAction = "Data quality focus cleared; all records are visible again.";
    table.scrollTop = 0;
    renderHierarchy();
    render();
  });
  overviewHeading.append(overviewTitle, clearFocus);
  overview.append(overviewHeading);

  const overviewRules = document.createElement("div");
  overviewRules.className = "overview-rules";
  for (const summary of currentValidationOverview.rules) {
    const row = document.createElement("article");
    row.className = "overview-rule";
    const ruleEvidence = document.createElement("div");
    const ruleLabel = document.createElement("strong");
    ruleLabel.textContent = summary.rule.label;
    const ruleMeta = document.createElement("span");
    ruleMeta.textContent = `${summary.rule.version} · ${summary.rule.status}`;
    ruleEvidence.append(ruleLabel, ruleMeta);
    const outcomes = document.createElement("div");
    outcomes.className = "overview-outcomes";
    for (const outcome of ["pass", "missing", "not-applicable", "refused"] as const) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = `${outcome} ${summary.counts[outcome]}`;
      button.setAttribute("aria-label", `Focus ${outcome} for ${summary.rule.label}`);
      button.disabled = summary.counts[outcome] === 0;
      button.classList.toggle(
        "active",
        currentValidationFocus?.ruleId === summary.rule.id && currentValidationFocus.outcome === outcome
      );
      button.addEventListener("click", () => {
        const result = evaluateLocalValidation(currentFixture.elements, summary.rule);
        const plan = planLocalValidationOutcome(currentFixture.elements, result, outcome);
        currentValidationResult = result;
        currentValidationFocus = {
          ruleId: summary.rule.id,
          outcome,
          elementIds: new Set(plan.matchedElementIds),
          plan
        };
        state.hierarchyFocus = null;
        state.selectedIds.clear();
        state.filter = "";
        state.typeFilter = null;
        state.outcomeFilter = null;
        filterInput.value = "";
        state.qualityAction = `Focused ${plan.matchedElementIds.length} ${outcome} records for ${summary.rule.label}: ${plan.editableElementIds.length} editable · ${plan.refusedElementIds.length} read-only or unsupported.`;
        table.scrollTop = 0;
        renderHierarchy();
        render();
      });
      outcomes.append(button);
    }
    row.append(ruleEvidence, outcomes);
    overviewRules.append(row);
  }
  overview.append(overviewRules);

  const typeOverview = document.createElement("section");
  typeOverview.className = "type-quality-overview";
  const typeHeading = document.createElement("div");
  typeHeading.className = "type-quality-heading";
  const typeTitle = document.createElement("strong");
  typeTitle.textContent = "Type quality summary";
  const typeNote = document.createElement("span");
  typeNote.textContent = `${currentValidationTypeSummary.length} types · missing/refused are candidate partial assessments`;
  typeHeading.append(typeTitle, typeNote);
  typeOverview.append(typeHeading);
  const typeHead = document.createElement("div");
  typeHead.className = "type-quality-row type-quality-head";
  for (const label of ["Type", "Records", "3D", "Editable", "Unmapped", "Unsupported", "Missing", "Refused"]) {
    const cell = document.createElement("span");
    cell.textContent = label;
    typeHead.append(cell);
  }
  typeOverview.append(typeHead);
  const typeRows = document.createElement("div");
  typeRows.className = "type-quality-rows";
  for (const item of currentValidationTypeSummary) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "type-quality-row";
    row.setAttribute("aria-label", `Focus type ${item.type}`);
    row.classList.toggle("active", state.hierarchyFocus?.nodeId === `type:${item.type}`);
    for (const value of [
      // item.type can be "" (see collectionType() in local-ifc-projection.ts); left unguarded this
      // rendered as a blank first cell in an otherwise-populated row (kaz, 2026-08-11).
      item.type === "" ? "(missing type)" : item.type,
      item.records,
      item.renderable,
      item.editable,
      item.unmapped,
      item.unsupported,
      item.counts.missing,
      item.counts.refused
    ]) {
      const cell = document.createElement("span");
      cell.textContent = String(value);
      row.append(cell);
    }
    row.addEventListener("click", () => focusHierarchy(item));
    typeRows.append(row);
  }
  typeOverview.append(typeRows);
  overview.append(typeOverview);

  if (currentValidationFocus !== null) {
    const focusActions = document.createElement("div");
    focusActions.className = "quality-focus-actions";
    const focusSummary = document.createElement("p");
    focusSummary.textContent = `${currentValidationFocus.plan.matchedElementIds.length} focused · ${currentValidationFocus.plan.editableElementIds.length} editable · ${currentValidationFocus.plan.refusedElementIds.length} read-only or unsupported`;
    const selectEditable = document.createElement("button");
    selectEditable.type = "button";
    selectEditable.textContent = `Select editable ${currentValidationFocus.outcome}`;
    selectEditable.disabled = currentValidationFocus.plan.editableElementIds.length === 0;
    selectEditable.addEventListener("click", () => {
      state.selectedIds.clear();
      for (const id of currentValidationFocus?.plan.editableElementIds ?? []) state.selectedIds.add(id);
      state.qualityAction = `Selected ${state.selectedIds.size} editable ${currentValidationFocus?.outcome ?? ""} records without selecting refused evidence.`;
      render();
    });
    focusActions.append(focusSummary, selectEditable);
    if (currentValidationFocus.outcome === "missing") {
      const rule = localValidationRules.find((candidate) => candidate.id === currentValidationFocus?.ruleId);
      const targetTool = rule?.target.kind === "classification"
        ? "codes"
        : rule?.target.kind === "component-reference"
          ? "components"
          : "inspect";
      const continueAction = document.createElement("button");
      continueAction.type = "button";
      continueAction.textContent = `Continue in ${targetTool === "codes" ? "Codes" : targetTool === "components" ? "Components" : "Inspect"}`;
      continueAction.disabled = currentValidationFocus.plan.editableElementIds.length === 0;
      continueAction.addEventListener("click", () => {
        state.selectedIds.clear();
        for (const id of currentValidationFocus?.plan.editableElementIds ?? []) state.selectedIds.add(id);
        state.activeTool = targetTool;
        render();
      });
      focusActions.append(continueAction);
    }
    overview.append(focusActions);
  }
  toolContent.append(overview);

  const validation = document.createElement("section");
  validation.className = "local-validation-tool";
  validation.innerHTML = `
    <h3>Candidate local rule coverage</h3>
    <p>Evaluates the current filtered record scope. Results remain partial and do not replace BIM-Hub validation authority.</p>
    <div class="library-action">
      <label>Candidate rule <select id="validation-rule" aria-label="Candidate validation rule"></select></label>
      <button id="evaluate-validation" type="button">Evaluate current scope</button>
    </div>
    <div id="validation-result"></div>`;
  toolContent.append(validation);
  const validationSelect = requiredElement<HTMLSelectElement>("validation-rule");
  for (const rule of localValidationRules) {
    const option = document.createElement("option");
    option.value = rule.id;
    option.textContent = `${rule.label} · ${rule.version}`;
    option.selected = currentValidationResult?.rule.id === rule.id;
    validationSelect.append(option);
  }
  const renderValidationResult = (): void => {
    const resultHost = requiredElement<HTMLDivElement>("validation-result");
    resultHost.replaceChildren();
    const result = currentValidationResult;
    if (result === null) return;
    const resultCensus = document.createElement("dl");
    resultCensus.className = "inspection-census validation-census";
    addDefinition(resultCensus, "Verdict", result.state);
    addDefinition(resultCensus, "Assessed", String(result.assessedRecords));
    addDefinition(resultCensus, "Pass", String(result.counts.pass));
    addDefinition(resultCensus, "Missing", String(result.counts.missing));
    addDefinition(resultCensus, "Not applicable", String(result.counts["not-applicable"]));
    addDefinition(resultCensus, "Refused", String(result.counts.refused));
    resultHost.append(resultCensus);

    const actionable = result.entries.filter((entry) => entry.outcome !== "pass");
    const boundedEntries = actionable.slice(0, 100);
    const results = document.createElement("div");
    results.className = "validation-results";
    for (const entry of boundedEntries) {
      const row = document.createElement("article");
      row.className = `validation-result outcome-${entry.outcome}`;
      const evidence = document.createElement("div");
      const heading = document.createElement("strong");
      heading.textContent = `${entry.outcome} · ${entry.elementId}`;
      const reason = document.createElement("span");
      reason.textContent = entry.reason;
      evidence.append(heading, reason);
      const open = document.createElement("button");
      open.type = "button";
      open.textContent = "Open record";
      open.addEventListener("click", () => {
        state.filter = entry.elementId;
        state.typeFilter = null;
        state.outcomeFilter = null;
        state.hierarchyFocus = null;
        currentValidationFocus = null;
        filterInput.value = entry.elementId;
        state.selectedIds.clear();
        state.selectedIds.add(entry.elementId);
        state.qualityAction = `Opened ${entry.elementId} from the ${result.rule.label} partial result.`;
        table.scrollTop = 0;
        renderHierarchy();
        render();
      });
      row.append(evidence, open);
      results.append(row);
    }
    if (boundedEntries.length > 0) resultHost.append(results);
    const retained = document.createElement("p");
    retained.className = "validation-retained";
    retained.textContent = `${actionable.length} non-pass outcomes retained; ${boundedEntries.length} shown in this bounded view.${actionable.length > boundedEntries.length ? ` ${actionable.length - boundedEntries.length} remain outside the DOM view.` : ""}`;
    resultHost.append(retained);
  };
  renderValidationResult();
  requiredElement<HTMLButtonElement>("evaluate-validation").addEventListener("click", () => {
    const rule = localValidationRules.find((candidate) => candidate.id === validationSelect.value);
    if (rule === undefined) return;
    currentValidationResult = evaluateLocalValidation(currentVisibleElements, rule);
    renderToolPanel();
  });

  const missing = document.createElement("section");
  missing.className = "missing-property-tool";
  missing.innerHTML = `
    <h3>Add missing properties</h3>
    <p>Only the current selection is assessed. Present and refused records are counted, never overwritten or dropped.</p>
    <div class="library-action">
      <label>Property <input id="missing-property" value="AssetTag" aria-label="Missing property name" /></label>
      <label>Value <input id="missing-value" value="candidate" aria-label="Missing property value" /></label>
      <button id="stage-missing" type="button">Add for missing selected</button>
    </div>
    <p id="quality-action" class="quality-action"></p>`;
  toolContent.append(missing);
  requiredElement<HTMLParagraphElement>("quality-action").textContent = state.qualityAction;
  requiredElement<HTMLButtonElement>("stage-missing").addEventListener("click", () => {
    const property = requiredElement<HTMLInputElement>("missing-property").value;
    const value = requiredElement<HTMLInputElement>("missing-value").value;
    try {
      const coverage = planMissingPropertyDraft(currentFixture.elements, state.selectedIds, property);
      if (coverage.eligibleElementIds.length === 0) {
        state.qualityAction = `Coverage: 0 staged · ${coverage.alreadyPresentElementIds.length} already present · ${coverage.refusedElementIds.length} refused.`;
        proposalOutput.dataset.state = "error";
        proposalOutput.textContent = "No editable selected records are missing this property.";
        renderToolPanel();
        return;
      }
      state.request.property = coverage.property;
      state.request.value = value;
      propertyInput.value = coverage.property;
      valueInput.value = value;
      // coverage.eligibleElementIds is already filtered to editable, property-missing elements, so
      // this never hits stageDraftChange's own refused count -- it's re-derived from the result
      // rather than assumed, so this stays true even if that filtering ever changes.
      const staged = stageDraftChange(
        currentFixture,
        state.draft,
        new Set(coverage.eligibleElementIds),
        coverage.property,
        value,
        "property"
      );
      state.draft = staged.ledger;
      state.qualityAction =
        `Coverage: ${staged.staged} staged · ${coverage.alreadyPresentElementIds.length} already present · ` +
        `${coverage.refusedElementIds.length + staged.refused} refused.`;
      proposalOutput.dataset.state = "ready";
      proposalOutput.textContent = JSON.stringify({ coverage, draft: state.draft }, null, 2);
      render();
      // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
      renderDraftLedger();
    } catch (error) {
      state.qualityAction = error instanceof Error ? error.message : "Missing-property planning failed.";
      proposalOutput.dataset.state = "error";
      proposalOutput.textContent = state.qualityAction;
      renderToolPanel();
    }
  });
}

function selectReportGroup(elementIds: readonly string[]): void {
  state.selectedIds.clear();
  for (const id of elementIds) state.selectedIds.add(id);
  render();
}

function reportMeasurementText(measurement: Report["groups"][number]["measurement"]): string {
  if (measurement.kind === "count") return `${measurement.measuredValue} items`;
  if (measurement.unitConflict) return "unsupported (mixed units)";
  const rounded = Math.round(measurement.measuredValue * 100) / 100;
  const unit = measurement.unit ?? "unit unknown";
  return measurement.measuredCount === 0 ? "no source quantity" : `${rounded} ${unit}`;
}

function downloadReportCsv(): void {
  const csv = reportToCsv(currentReport);
  const bytes = new TextEncoder().encode(csv);
  downloadLocalBytes(bytes, "aec-data-workbench-report.csv", "text/csv;charset=utf-8");
}

function refreshCurrentReport(): void {
  currentReport = buildReport(
    currentFixture.id,
    currentFixture.label,
    currentFixture.elements,
    currentLocalIfcObservation,
    reportGroupingRules,
    reportDisplayColumns
  );
}

function setReportGroupingRules(next: readonly ReportGroupingKeyId[]): void {
  reportGroupingRules = next;
  refreshCurrentReport();
  render();
}

function setReportDisplayColumns(next: readonly string[]): void {
  reportDisplayColumns = next;
  refreshCurrentReport();
  render();
}

function renderReportGroupingControls(available: readonly ReportGroupingKeyDescriptor[]): void {
  const list = requiredElement<HTMLDivElement>("report-grouping-rules");
  list.replaceChildren();
  reportGroupingRules.forEach((keyId, index) => {
    const chip = document.createElement("span");
    chip.className = "report-grouping-rule";
    const label = document.createElement("span");
    label.textContent = groupingKeyLabel(keyId);
    chip.append(label);
    const up = document.createElement("button");
    up.type = "button";
    up.textContent = "↑";
    up.setAttribute("aria-label", `Move ${groupingKeyLabel(keyId)} earlier`);
    up.disabled = index === 0;
    up.addEventListener("click", () => setReportGroupingRules(moveGroupingRule(reportGroupingRules, keyId, "up")));
    const down = document.createElement("button");
    down.type = "button";
    down.textContent = "↓";
    down.setAttribute("aria-label", `Move ${groupingKeyLabel(keyId)} later`);
    down.disabled = index === reportGroupingRules.length - 1;
    down.addEventListener("click", () => setReportGroupingRules(moveGroupingRule(reportGroupingRules, keyId, "down")));
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = "×";
    remove.setAttribute("aria-label", `Remove ${groupingKeyLabel(keyId)} grouping level`);
    remove.addEventListener("click", () => setReportGroupingRules(removeGroupingRule(reportGroupingRules, keyId)));
    chip.append(up, down, remove);
    list.append(chip);
  });

  const addSelect = requiredElement<HTMLSelectElement>("report-grouping-add-key");
  addSelect.replaceChildren();
  const addable = available.filter((descriptor) => !reportGroupingRules.includes(descriptor.id));
  for (const descriptor of addable) {
    const option = document.createElement("option");
    option.value = descriptor.id;
    option.textContent = descriptor.label;
    addSelect.append(option);
  }
  const addButton = requiredElement<HTMLButtonElement>("report-grouping-add");
  addButton.disabled = addable.length === 0;
  addSelect.disabled = addable.length === 0;
  addButton.addEventListener("click", () => {
    if (addSelect.value === "") return;
    setReportGroupingRules(addGroupingRule(reportGroupingRules, addSelect.value as ReportGroupingKeyId, available));
  });
}

/** Add／remove UI for display columns (see domain/report.ts's `ReportColumnValue` doc comment).
 * Deliberately its own chip list distinct from `renderReportGroupingControls` — a display column
 * shows a property's value on each row without repartitioning it, which is a different concept
 * from a grouping level, so the two never share a chip list or an add-control. */
function renderReportDisplayColumnControls(available: readonly string[]): void {
  const list = requiredElement<HTMLDivElement>("report-display-columns");
  list.replaceChildren();
  reportDisplayColumns.forEach((propertyName) => {
    const chip = document.createElement("span");
    chip.className = "report-display-column";
    const label = document.createElement("span");
    label.textContent = propertyName;
    chip.append(label);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = "×";
    remove.setAttribute("aria-label", `Remove ${propertyName} display column`);
    remove.addEventListener("click", () => setReportDisplayColumns(removeDisplayColumn(reportDisplayColumns, propertyName)));
    chip.append(remove);
    list.append(chip);
  });

  const addSelect = requiredElement<HTMLSelectElement>("report-display-column-add-key");
  addSelect.replaceChildren();
  const addable = available.filter((propertyName) => !reportDisplayColumns.includes(propertyName));
  for (const propertyName of addable) {
    const option = document.createElement("option");
    option.value = propertyName;
    option.textContent = propertyName;
    addSelect.append(option);
  }
  const addButton = requiredElement<HTMLButtonElement>("report-display-column-add");
  addButton.disabled = addable.length === 0;
  addSelect.disabled = addable.length === 0;
  addButton.addEventListener("click", () => {
    if (addSelect.value === "") return;
    setReportDisplayColumns(addDisplayColumn(reportDisplayColumns, addSelect.value, available));
  });
}

function renderReportTool(): void {
  const available = availableGroupingKeys(currentFixture.elements);
  const availableColumns = availableDisplayColumns(currentFixture.elements);
  const headingPath = reportGroupingRules.length === 0
    ? "Ungrouped"
    : reportGroupingRules.map((id) => groupingKeyLabel(id)).join(" → ");
  toolContent.innerHTML = `
    <div class="tool-heading">
      <div><p>${headingPath} quantity takeoff from the active dataset.</p></div>
      <span>local · read-only</span>
    </div>
    <div class="report-note">
      <p>Length／Area／Volume are summed only from source IFC quantity sets (<code>Qto_*</code>) when they are
      present in the observed model; records without one are counted as unmeasured rather than approximated
      from plan geometry. Click a row to select its records in the 3D view and virtualized table.</p>
      <button id="report-export-csv" type="button">Export report CSV</button>
    </div>
    <div class="report-grouping">
      <div id="report-grouping-rules" class="report-grouping-rules"></div>
      <div class="report-grouping-add-row">
        <select id="report-grouping-add-key" aria-label="Add grouping level"></select>
        <button id="report-grouping-add" type="button">Add level</button>
      </div>
    </div>
    <div class="report-display-column-controls">
      <span class="report-display-column-controls-label">Display columns</span>
      <div id="report-display-columns" class="report-display-columns"></div>
      <div class="report-display-column-add-row">
        <select id="report-display-column-add-key" aria-label="Add display column"></select>
        <button id="report-display-column-add" type="button">Add column</button>
      </div>
    </div>`;

  renderReportGroupingControls(available);
  renderReportDisplayColumnControls(availableColumns);

  const reportTable = document.createElement("section");
  reportTable.className = "report-table";
  const columnCount = reportGroupingRules.length + reportDisplayColumns.length + 4;
  reportTable.style.setProperty("--report-columns", String(Math.max(columnCount, 1)));

  const scroll = document.createElement("div");
  scroll.className = "report-scroll";

  const head = document.createElement("div");
  head.className = "report-row report-head";
  const headLabels = [
    ...reportGroupingRules.map(groupingKeyLabel),
    ...reportDisplayColumns,
    "Measurement",
    "Renderable",
    "Unrendered plan",
    "Producer skipped"
  ];
  for (const label of headLabels) {
    const cell = document.createElement("span");
    cell.textContent = label;
    head.append(cell);
  }
  scroll.append(head);

  const reportRows = document.createElement("div");
  reportRows.className = "report-rows";
  if (currentReport.groups.length === 0) {
    const empty = document.createElement("p");
    empty.className = "report-empty";
    empty.textContent = "No records are available to group in the active dataset.";
    reportRows.append(empty);
  }

  for (const group of currentReport.groups) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "report-row";
    const pathText = group.path.map((segment) => segment.value).join(" · ") || "(all records)";
    row.setAttribute("aria-label", `Select ${group.elementIds.length} elements for ${pathText}`);
    for (const value of [
      ...group.path.map((segment) => segment.value),
      ...reportDisplayColumns.map((name) => formatReportColumnValue(group.columnValues.get(name) ?? { kind: "absent" })),
      reportMeasurementText(group.measurement),
      group.accounting.renderable,
      group.accounting.unrenderedPlanEvidence,
      group.accounting.producerSkipped
    ]) {
      const cell = document.createElement("span");
      cell.textContent = String(value);
      row.append(cell);
    }
    row.title = `${group.measurement.unmeasuredCount} unmeasured · ${group.measurement.unsupportedUnitCount} unsupported unit`;
    row.addEventListener("click", () => selectReportGroup(group.elementIds));
    row.addEventListener("dblclick", () => selectReportGroup(group.elementIds));
    reportRows.append(row);
  }
  scroll.append(reportRows);
  reportTable.append(scroll);
  toolContent.append(reportTable);

  requiredElement<HTMLButtonElement>("report-export-csv").addEventListener("click", downloadReportCsv);
}

function resetLocalExport(message = ""): void {
  currentRegeneratedIfc = null;
  localExportState = "idle";
  localExportMessage = message;
  localSaveWorking = false;
}

function downloadLocalBytes(bytes: Uint8Array, filename: string, type: string): void {
  const owned = new Uint8Array(bytes);
  const url = URL.createObjectURL(new Blob([owned.buffer], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function localSaveOutcomeMessage(outcome: LocalExportSaveOutcome): string {
  switch (outcome.state) {
    case "saved":
      return `${outcome.filename} saved and verified (${formatBytes(outcome.byteLength ?? 0)}).`;
    case "cancelled":
      return "Save cancelled. No file was written.";
    case "refused-existing":
      return `${outcome.filename} already exists. Choose a new filename; overwrite is not enabled.`;
    case "cleanup-failed":
      return `${outcome.filename} was not verified and cleanup failed. Inspect that file before continuing.`;
    case "failed":
      return `${outcome.filename} was not saved. Any partial file was removed.`;
  }
}

function directoryExportOutcomeMessage(outcome: DirectoryExportOutcome): string {
  if (outcome.state === "cancelled") return "Folder export cancelled. No file was written.";
  if (outcome.state === "failed") return "Folder export failed. No file was written.";
  const saved = outcome.items.filter((item) => item.state === "saved").length;
  const refused = outcome.items.filter((item) => item.state === "refused-existing");
  const failed = outcome.items.filter((item) => item.state === "failed" || item.state === "cleanup-failed");
  const parts = [`${saved} of ${outcome.items.length} saved and verified to the chosen folder.`];
  if (refused.length > 0) parts.push(`Already existed and was skipped: ${refused.map((item) => item.filename).join(", ")}.`);
  if (failed.length > 0) parts.push(`Not saved: ${failed.map((item) => item.filename).join(", ")}.`);
  return parts.join(" ");
}

/** Bundles the regenerated IFC, its receipt, and the original IFC into one user-chosen directory. */
async function deliverLocalDirectoryExport(): Promise<void> {
  if (currentRegeneratedIfc === null) return;
  const artifacts: DirectoryExportArtifact[] = [
    { filename: currentRegeneratedIfc.receipt.output.filename, bytes: currentRegeneratedIfc.ifcBytes },
    {
      filename: `${currentRegeneratedIfc.receipt.output.filename}.receipt.json`,
      bytes: new TextEncoder().encode(`${JSON.stringify(currentRegeneratedIfc.receipt, null, 2)}\n`)
    }
  ];
  if (currentLocalSource !== null) artifacts.push({ filename: currentLocalSource.filename, bytes: currentLocalSource.bytes });

  directoryExportWorking = true;
  directoryExportMessage = "Choose a folder for the regenerated IFC, receipt, and original IFC.";
  renderLocalExportDialog();
  try {
    directoryExportMessage = directoryExportOutcomeMessage(
      await exportNativeLocalDirectory("Choose a folder for the local export", artifacts)
    );
  } catch {
    directoryExportMessage = "Folder export failed. No file was written.";
  } finally {
    directoryExportWorking = false;
    renderLocalExportDialog();
  }
}

async function deliverLocalArtifact(artifact: LocalExportArtifact, mediaType: string): Promise<void> {
  if (!nativeLocalShell) {
    downloadLocalBytes(artifact.bytes, artifact.filename, mediaType);
    return;
  }
  localSaveWorking = true;
  localExportMessage = `Choose a new filename for ${artifact.filename}.`;
  renderLocalExportDialog();
  try {
    localExportMessage = localSaveOutcomeMessage(await saveNativeLocalExport(artifact));
  } catch {
    localExportMessage = `${artifact.filename} was not saved.`;
  } finally {
    localSaveWorking = false;
    renderLocalExportDialog();
  }
}

function boundedExportReceipt(result: ProjectionRegenerationResult): Readonly<Record<string, unknown>> {
  const receipt = result.receipt;
  return {
    label: receipt.label,
    source: receipt.source,
    output: receipt.output,
    artifact: receipt.artifact,
    input: {
      baselinePlanSha256: receipt.input.baselinePlanSha256,
      modifiedPlanSha256: receipt.input.modifiedPlanSha256,
      proposalSha256: receipt.input.proposalSha256,
      projectionCoverage: receipt.input.projectionCoverage
    },
    operations: receipt.operations,
    validation: {
      baselineIssueCount: receipt.validation.baselineIssueCount,
      modifiedIssueCount: receipt.validation.modifiedIssueCount,
      blockingIssueCount: receipt.validation.blockingIssueCount
    },
    reimport: {
      requestedChangesFoundExactlyOnce: receipt.reimport.requestedChangesFoundExactlyOnce,
      planLeafClosure: receipt.reimport.planLeafClosure
    },
    limitations: receipt.limitations,
    resource: receipt.resource
  };
}

async function regeneratePendingLocalIfc(): Promise<void> {
  if (currentLocalSource === null || currentLocalIfcObservation === null) {
    localExportState = "error";
    localExportMessage = "Open and observe an IFC before regenerating a local export.";
    renderLocalExportDialog();
    return;
  }
  localExportState = "working";
  localExportMessage = "Validating the producer Plan, regenerating IFC, and reimporting the result…";
  renderLocalExportDialog();
  try {
    const { loadPinnedProjectionRegenerationEngine } = await import("./integration/local-ifc-regeneration-wasm");
    const engine = await loadPinnedProjectionRegenerationEngine();
    currentRegeneratedIfc = await regenerateLocalIfcProjection(
      engine,
      currentLocalSource,
      currentLocalIfcObservation,
      currentFixture,
      currentProjectionCoverage,
      state.draft
    );
    localExportState = "ready";
    localExportMessage = `Receiver checks passed. Review the limitations and acknowledge the projection boundary before ${nativeLocalShell ? "saving" : "download"}.`;
  } catch (error) {
    currentRegeneratedIfc = null;
    localExportState = "error";
    localExportMessage = error instanceof Error ? error.message : "Local IFC regeneration failed.";
  }
  renderLocalExportDialog();
}

function renderLocalExport(): HTMLElement {
  const section = document.createElement("section");
  section.className = "local-export";
  const title = document.createElement("div");
  title.className = "local-export-heading";
  title.innerHTML = `<div><strong>Local IFC export</strong><span>Local export — not saved or verified by BIM-Hub.</span></div><span>projection regeneration</span>`;
  section.append(title);

  const controls = document.createElement("div");
  controls.className = "local-export-controls";
  const regenerate = document.createElement("button");
  regenerate.type = "button";
  regenerate.textContent = localExportState === "working" ? "Regenerating…" : "Regenerate IFC";
  regenerate.disabled = currentLocalSource === null || currentLocalIfcObservation?.state !== "observed" || state.draft.entries.length === 0 || localExportState === "working" || localSaveWorking;
  regenerate.addEventListener("click", () => void regeneratePendingLocalIfc());
  controls.append(regenerate);

  const original = document.createElement("button");
  original.type = "button";
  original.textContent = nativeLocalShell ? "Save original IFC" : "Download original IFC";
  original.disabled = currentLocalSource === null || localSaveWorking;
  original.addEventListener("click", () => {
    if (currentLocalSource !== null) void deliverLocalArtifact({
      kind: "original-ifc",
      filename: currentLocalSource.filename,
      bytes: currentLocalSource.bytes
    }, "application/x-step");
  });
  controls.append(original);
  section.append(controls);

  const status = document.createElement("p");
  status.className = "local-export-status";
  status.dataset.state = localExportState;
  status.textContent = localExportMessage || (currentLocalSource === null
    ? "Open a local IFC to enable source-bound property export."
    : state.draft.entries.length === 0
      ? "Add an existing occurrence property replacement to enable regeneration."
      : "Only existing occurrence property replacements of the same value type are eligible.");
  section.append(status);

  if (currentRegeneratedIfc !== null) {
    const boundary = document.createElement("label");
    boundary.className = "local-export-acknowledgement";
    const acknowledgement = document.createElement("input");
    acknowledgement.type = "checkbox";
    const copy = document.createElement("span");
    copy.textContent = "I understand this IFC is regenerated from the supported BIM-JSON projection and is not a source whole-record lossless round trip.";
    boundary.append(acknowledgement, copy);
    section.append(boundary);

    const resultControls = document.createElement("div");
    resultControls.className = "local-export-controls";
    const downloadIfc = document.createElement("button");
    downloadIfc.type = "button";
    downloadIfc.textContent = nativeLocalShell ? "Save regenerated IFC" : "Download regenerated IFC";
    downloadIfc.disabled = true;
    downloadIfc.addEventListener("click", () => {
      if (currentRegeneratedIfc !== null) void deliverLocalArtifact({
        kind: "regenerated-ifc",
        filename: currentRegeneratedIfc.receipt.output.filename,
        bytes: currentRegeneratedIfc.ifcBytes
      }, "application/x-step");
    });
    const downloadReceipt = document.createElement("button");
    downloadReceipt.type = "button";
    downloadReceipt.textContent = nativeLocalShell ? "Save receipt" : "Download receipt";
    downloadReceipt.disabled = true;
    downloadReceipt.addEventListener("click", () => {
      if (currentRegeneratedIfc === null) return;
      const bytes = new TextEncoder().encode(`${JSON.stringify(currentRegeneratedIfc.receipt, null, 2)}\n`);
      void deliverLocalArtifact({
        kind: "verification-receipt",
        filename: `${currentRegeneratedIfc.receipt.output.filename}.receipt.json`,
        bytes
      }, "application/json");
    });
    resultControls.append(downloadIfc, downloadReceipt);
    if (nativeLocalShell) {
      const saveFolder = document.createElement("button");
      saveFolder.type = "button";
      saveFolder.textContent = directoryExportWorking ? "Saving to folder…" : "Save all to folder";
      saveFolder.disabled = true;
      saveFolder.addEventListener("click", () => void deliverLocalDirectoryExport());
      resultControls.append(saveFolder);
      acknowledgement.addEventListener("change", () => {
        downloadIfc.disabled = !acknowledgement.checked;
        downloadReceipt.disabled = !acknowledgement.checked;
        saveFolder.disabled = !acknowledgement.checked || directoryExportWorking;
      });
    } else {
      acknowledgement.addEventListener("change", () => {
        downloadIfc.disabled = !acknowledgement.checked;
        downloadReceipt.disabled = !acknowledgement.checked;
      });
    }
    section.append(resultControls);
    if (directoryExportMessage !== "") {
      const directoryStatus = document.createElement("p");
      directoryStatus.className = "local-export-status";
      directoryStatus.textContent = directoryExportMessage;
      section.append(directoryStatus);
    }

    const details = document.createElement("details");
    details.className = "local-export-receipt";
    const summary = document.createElement("summary");
    summary.textContent = "Regeneration receipt";
    const pre = document.createElement("pre");
    pre.textContent = JSON.stringify(boundedExportReceipt(currentRegeneratedIfc), null, 2);
    details.append(summary, pre);
    section.append(details);
  }
  return section;
}

function renderLocalExportDialog(): void {
  localExportSurface.replaceChildren(renderLocalExport());
}

function proposalPopulationCounts(
  proposal: D2LocalEnrichmentProposal | D2LocalClassificationProposal
): readonly (readonly [string, number])[] {
  return [
    ["Selected", proposal.selectedPopulation.selected],
    ["Proposed", proposal.selectedPopulation.proposed],
    ["Refused", proposal.selectedPopulation.refused],
    ["Writer supported", proposal.writerAdmission.supported],
    ["Writer unsupported", proposal.writerAdmission.unsupported]
  ];
}

function renderLocalProposalSummary(
  container: HTMLElement,
  title: string,
  binding: LocalSourceBinding,
  proposal: D2LocalEnrichmentProposal | D2LocalClassificationProposal | null,
  note: string
): void {
  const section = document.createElement("section");
  section.className = "local-proposal-summary";
  const heading = document.createElement("div");
  heading.className = "local-proposal-summary-heading";
  const strong = document.createElement("strong");
  strong.textContent = title;
  const stateLabel = document.createElement("span");
  stateLabel.textContent = !binding.bound
    ? "not bound to a local IFC source"
    : proposal === null
      ? "computing…"
      : `${proposal.status} · authority: ${proposal.authority}`;
  heading.append(strong, stateLabel);
  section.append(heading);

  if (!binding.bound) {
    const unbound = document.createElement("p");
    unbound.textContent = "Open and observe a local IFC to bind this proposal to a source receipt. No digest is fabricated while unbound.";
    section.append(unbound);
    container.append(section);
    return;
  }
  if (proposal !== null) {
    const counts = document.createElement("dl");
    counts.className = "local-proposal-counts";
    for (const [label, value] of proposalPopulationCounts(proposal)) {
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = String(value);
      counts.append(term, description);
    }
    section.append(counts);
    const summaryNote = document.createElement("p");
    summaryNote.textContent = proposal.status === "local-refusal"
      ? "Refused as a whole population; 0 operations were authored. No target was silently partially applied."
      : note;
    section.append(summaryNote);
  }
  container.append(section);
}

/**
 * Renders the D3 save outcome for the *last* attempt against the *current* fixture, distinct from
 * whatever `currentD3CommitPayload` (a locally-built, not-yet-sent preview) says right now —
 * condition 3 of the GO answer requires a server receipt and an unsaved proposal to never be
 * conflated on screen. Three mutually exclusive states, each its own CSS class so they are also
 * structurally distinguishable in a DOM test, not merely by wording:
 *   - `.d3-save-receipt`: a real BIM-Hub commit happened THIS session (`d3SaveState === "saved"`).
 *   - `.d3-save-error`: the last attempt THIS session failed, with the typed code/message BIM-Hub
 *     or the BFF returned (never a generic "failed").
 *   - `.d3-save-known`: no attempt happened this session, but a prior session already saved this
 *     fixture (`localStorage`, see `integration/d3-save-persistence.ts`) — this is what "reload the
 *     page, still see the same revision" renders as.
 * Returns `null` when none of the three apply (nothing has ever been saved for this fixture).
 */
function renderD3SaveOutcome(): HTMLElement | null {
  if (d3SaveState === "saved" && d3SaveReceipt !== null) {
    const facts = d3SaveReceipt;
    const receiptElement = document.createElement("div");
    receiptElement.className = "d3-save-receipt";
    const heading = document.createElement("strong");
    heading.textContent = "Saved to BIM-Hub";
    const summary = document.createElement("p");
    summary.textContent =
      `Workbench proposal revision ${facts.committedRevision} is stored in BIM-Hub project ${facts.projectId}, ` +
      `model ${facts.modelId}. This did not apply anything to a Model's authoritative content — it saved ` +
      "the proposal revision itself, for BIM-Hub's own review/apply flow.";
    const digests = document.createElement("dl");
    digests.className = "d3-commit-review-counts";
    for (const [label, value] of [
      ["Committed revision", String(facts.committedRevision)],
      ["Manifest SHA-256", facts.manifestSha256],
      ["Payload SHA-256", facts.payloadSha256],
      ["Payload bytes", String(facts.payloadBytes)]
    ] as const) {
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = value;
      digests.append(term, description);
    }
    receiptElement.append(heading, summary, digests);
    return receiptElement;
  }
  if (d3SaveState === "error" && d3SaveError !== null) {
    const error = document.createElement("div");
    error.className = "d3-save-error";
    error.dataset.code = d3SaveError.code;
    const heading = document.createElement("strong");
    heading.textContent = "BIM-Hub save failed";
    const detail = document.createElement("p");
    detail.textContent = `${d3SaveError.code}: ${d3SaveError.message}`;
    error.append(heading, detail);
    return error;
  }
  const known = loadKnownD3SaveTarget(currentFixture.id);
  if (known !== null) {
    const info = document.createElement("div");
    info.className = "d3-save-known";
    info.textContent =
      `Last saved to BIM-Hub project ${known.projectId}, model ${known.modelId}, revision ${known.revision} ` +
      "(remembered locally in this browser; BIM-Hub itself remains the source of truth for the Model).";
    return info;
  }
  return null;
}

/**
 * A fixture's first save has no known Project/Model yet, so the manifest `buildModelStoragePlan`
 * builds cannot embed real ids -- and BIM-Hub's canonical-scope validation refuses a manifest whose
 * `project_id`/`model_id` don't exactly match the Project/Model the chunk-upload-session actually
 * starts against (see `ensureD3SaveTarget`'s doc comment in `src/server/d3-save.ts` for the full
 * story: a real kaz save attempt hit exactly this, "client upload manifest failed canonical scope
 * validation", 2026-08-12). This calls the new `/d3/ensure-target` endpoint to create (or idempotently
 * reuse) them first, so `attemptD3Save` always has real ids before it ever builds a manifest.
 */
type EnsureTargetOutcome =
  | { readonly ok: true; readonly projectId: string; readonly modelId: string }
  | { readonly ok: false; readonly code: string; readonly message: string };

async function ensureD3SaveTargetOverNetwork(fixtureId: string): Promise<EnsureTargetOutcome> {
  let response: Response;
  try {
    response = await fetch("/d3/ensure-target", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ fixtureId })
    });
  } catch (cause) {
    return { ok: false, code: "network-error", message: cause instanceof Error ? cause.message : String(cause) };
  }
  let result: unknown;
  try {
    result = await response.json();
  } catch {
    return { ok: false, code: "server-error", message: "The save-target response was not valid JSON." };
  }
  const parsed = result as D3EnsureTargetResult | null;
  if (parsed !== null && typeof parsed === "object" && parsed.ok === true) {
    return { ok: true, projectId: parsed.projectId, modelId: parsed.modelId };
  }
  return {
    ok: false,
    code: (parsed as { readonly code?: string } | null)?.code ?? `http-${response.status}`,
    message: (parsed as { readonly message?: string } | null)?.message ?? `Save-target preparation failed (HTTP ${response.status}).`
  };
}

/**
 * Reads the current manifest+content back from BIM-Hub (via `functions/d3/read-model-snapshot.ts`,
 * never a token — see that file's own doc comment) for the read-hydrate-update sequence a save
 * against a known target with a real prior revision needs. See `src/server/d3-save.ts`'s own module
 * doc comment for the full root-cause story of why `.create()` alone is only correct for a model's
 * first-ever revision.
 */
async function readModelSnapshotOverNetwork(projectId: string, modelId: string, revision: number): Promise<D3ReadModelSnapshotResult> {
  let response: Response;
  try {
    response = await fetch("/d3/read-model-snapshot", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ projectId, modelId, revision })
    });
  } catch (cause) {
    return { ok: false, code: "server-error", message: cause instanceof Error ? cause.message : String(cause) };
  }
  let result: unknown;
  try {
    result = await response.json();
  } catch {
    return { ok: false, code: "server-error", message: "The model-snapshot read response was not valid JSON." };
  }
  const parsed = result as D3ReadModelSnapshotResult | null;
  if (parsed !== null && typeof parsed === "object" && parsed.ok === true) return parsed;
  const failure = parsed as Extract<D3ReadModelSnapshotResult, { readonly ok: false }> | null;
  return {
    ok: false,
    code: failure?.code ?? "server-error",
    message: failure?.message ?? `Model-snapshot read failed (HTTP ${response.status}).`
  };
}

/**
 * Builds the manifest/chunks for a save against `target`, and — only when `target.revision` is a
 * real prior revision, never on a fixture's first save — the `pageChanges` BIM-Hub's own
 * `begin_client_chunk_upload` guard requires alongside a non-null `base_revision`. A `target.revision
 * === null` known target (an `ensureD3SaveTargetOverNetwork` result with nothing committed yet) still
 * goes through `.create()`, same as a fully-unknown fixture, since there is no prior manifest to
 * hydrate or diff against.
 */
async function buildD3SavePlan(
  target: { readonly projectId: string; readonly modelId: string; readonly revision: number | null },
  payload: D3CommitPayload
): Promise<{ readonly plan: ModelStorageResult; readonly pageChanges: ModelPageChanges | null }> {
  const revisionValue = target.revision === null ? 1 : target.revision + 1;
  if (target.revision === null) {
    return { plan: await buildModelStoragePlan(target.projectId, target.modelId, revisionValue, payload.payloadJson), pageChanges: null };
  }

  const baseRevision = target.revision;
  const snapshot = await readModelSnapshotOverNetwork(target.projectId, target.modelId, baseRevision);
  if (!snapshot.ok) {
    return { plan: { ok: false, reason: "session-build-failed", detail: `${snapshot.code}: ${snapshot.message}` }, pageChanges: null };
  }

  const contentObjects = snapshot.contentObjects.map((entry) => ({ digest: entry.digest, bytes: base64ToBytes(entry.bytesBase64) }));
  const plan = await hydrateAndUpdateModelStoragePlan(
    snapshot.manifestJson,
    snapshot.manifestSha256,
    contentObjects,
    revisionValue,
    payload.payloadJson
  );
  if (!plan.ok) return { plan, pageChanges: null };

  try {
    const baseManifest = parseModelChunkManifest(JSON.parse(snapshot.manifestJson), {
      projectId: target.projectId,
      modelId: target.modelId,
      revision: baseRevision
    });
    const targetManifest = parseModelChunkManifest(JSON.parse(plan.plan.manifestJson), {
      projectId: target.projectId,
      modelId: target.modelId,
      revision: revisionValue
    });
    return { plan, pageChanges: diffModelChunkPages(baseManifest, targetManifest) };
  } catch (cause) {
    return {
      plan: { ok: false, reason: "session-build-failed", detail: `Model manifest diff failed: ${cause instanceof Error ? cause.message : String(cause)}` },
      pageChanges: null
    };
  }
}

/**
 * Attempts one real D3 save: builds the BIM-Hub `bim-hub.model-chunks/1` manifest/chunks locally
 * (`integration/bim-hub-model-storage.ts`, no token involved), then POSTs them — never a token — to
 * the Workbench BFF (`functions/d3/save.ts`), which is the only place an Identity or BIM-Hub token
 * exists (condition 1 of the GO answer). `fixture`/`payload` are captured at click time so a
 * concurrent proposal edit mid-flight cannot silently change what gets saved out from under this
 * attempt; a stale attempt (a newer one started before this one resolved) is discarded via
 * `d3SaveToken`, the same pattern `d3CommitPayloadToken` etc. already use in this file.
 */
async function attemptD3Save(fixture: AnonymousFixture, payload: D3CommitPayload): Promise<void> {
  const token = ++d3SaveToken;
  d3SaveState = "saving";
  d3SaveError = null;
  renderDraftLedger();

  const known: D3KnownSaveTarget | null = loadKnownD3SaveTarget(fixture.id);
  let target: { readonly projectId: string; readonly modelId: string; readonly revision: number | null };
  if (known === null) {
    const ensured = await ensureD3SaveTargetOverNetwork(fixture.id);
    if (token !== d3SaveToken) return;
    if (!ensured.ok) {
      d3SaveState = "error";
      d3SaveError = { code: ensured.code, message: ensured.message };
      renderDraftLedger();
      return;
    }
    target = { projectId: ensured.projectId, modelId: ensured.modelId, revision: null };
  } else {
    target = known;
  }

  const { plan, pageChanges } = await buildD3SavePlan(target, payload);
  if (token !== d3SaveToken) return;
  if (!plan.ok) {
    d3SaveState = "error";
    d3SaveError = { code: plan.reason, message: plan.detail };
    renderDraftLedger();
    return;
  }

  const requestBody = {
    fixtureId: fixture.id,
    known: target,
    pageChanges,
    idempotencyKey: payload.idempotencyKey,
    manifestJson: plan.plan.manifestJson,
    manifestSha256: plan.plan.manifestSha256,
    payloadSha256: plan.plan.payloadSha256,
    payloadBytes: plan.plan.payloadBytes,
    chunks: plan.plan.chunks.map((chunk) => ({ digest: chunk.digest, bytesBase64: bytesToBase64(chunk.bytes) }))
  };

  let response: Response;
  try {
    response = await fetch("/d3/save", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(requestBody)
    });
  } catch (cause) {
    if (token !== d3SaveToken) return;
    d3SaveState = "error";
    d3SaveError = { code: "network-error", message: cause instanceof Error ? cause.message : String(cause) };
    renderDraftLedger();
    return;
  }
  if (token !== d3SaveToken) return;

  let result: unknown;
  try {
    result = await response.json();
  } catch {
    d3SaveState = "error";
    d3SaveError = { code: "server-error", message: "The save response was not valid JSON." };
    renderDraftLedger();
    return;
  }
  const parsed = result as D3SaveResult | null;
  if (parsed !== null && typeof parsed === "object" && parsed.ok === true) {
    // Only a full success is remembered locally: a project/model that got created but never reached
    // a committed revision has no valid revision number to persist, and persisting nothing here is
    // simpler and safer than inventing one — a retry starts a fresh first save instead.
    persistKnownD3SaveTarget(fixture.id, { projectId: parsed.projectId, modelId: parsed.modelId, revision: parsed.committedRevision });
    d3SaveState = "saved";
    d3SaveReceipt = parsed;
    renderDraftLedger();
    return;
  }
  d3SaveState = "error";
  d3SaveError = {
    code: parsed?.code ?? `http-${response.status}`,
    message: parsed?.message ?? `BIM-Hub save failed (HTTP ${response.status}).`
  };
  renderDraftLedger();
}

/**
 * One property-proposal submission for a single staged `"replace"` entry -- see this file's own
 * "D2-C property proposal" module doc comment above (near `propertyProposalSubmitting`'s
 * declaration) for the overall design. `known`/`eligibility` are re-derived from scratch here (not
 * threaded through from the caller) so a stale click -- the fixture or pending changes changed
 * between render and click -- can never submit against a target/value the UI is no longer showing;
 * `renderDraftLedger`'s own gating (see the entry-row loop below) keeps the button disabled whenever
 * either would fail, so reaching here with either `null`/`ineligible` should not normally happen, but
 * this function refuses closed rather than assuming the button's own gating is airtight.
 */
async function attemptPropertyProposal(entry: DraftEntry): Promise<void> {
  const key = propertyProposalEntryKey(entry.elementId, entry.property);
  if (propertyProposalSubmitting.has(key)) return;
  const known = loadKnownD3SaveTarget(currentFixture.id);
  if (known === null || known.revision === null) return;
  const eligibility = evaluateDraftEntryForProposal(entry);
  if (!eligibility.eligible) return;

  propertyProposalSubmitting.add(key);
  propertyProposalOutcomes.delete(key);
  renderDraftLedger();

  let request: Awaited<ReturnType<typeof buildPropertyProposalRequest>>;
  try {
    request = await buildPropertyProposalRequest({
      projectId: known.projectId,
      modelId: known.modelId,
      baseRevision: known.revision,
      sourceDocumentId: null,
      occurrenceGuid: entry.elementId,
      propertyPath: entry.property,
      before: eligibility.before,
      after: eligibility.after,
      clientCapabilityVersion: "workbench/2026-08-13"
    });
  } catch (error) {
    propertyProposalSubmitting.delete(key);
    propertyProposalOutcomes.set(key, {
      ok: false,
      code: "invalid-request",
      message: error instanceof Error ? error.message : String(error)
    });
    renderDraftLedger();
    return;
  }

  let response: Response;
  try {
    response = await fetch("/d3/property-proposal", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(request)
    });
  } catch (cause) {
    propertyProposalSubmitting.delete(key);
    propertyProposalOutcomes.set(key, {
      ok: false,
      code: "network-error",
      message: cause instanceof Error ? cause.message : String(cause)
    });
    renderDraftLedger();
    return;
  }

  let result: unknown;
  try {
    result = await response.json();
  } catch {
    propertyProposalSubmitting.delete(key);
    propertyProposalOutcomes.set(key, { ok: false, code: "server-error", message: "The property-proposal response was not valid JSON." });
    renderDraftLedger();
    return;
  }
  propertyProposalSubmitting.delete(key);
  const parsed = result as PropertyProposalSaveResult | null;
  if (parsed !== null && typeof parsed === "object" && parsed.ok === true) {
    propertyProposalOutcomes.set(key, {
      ok: true,
      status: parsed.status,
      targetRevision: parsed.receipt.targetRevision,
      manifestSha256: parsed.receipt.manifestSha256
    });
  } else {
    propertyProposalOutcomes.set(key, {
      ok: false,
      code: parsed?.code ?? `http-${response.status}`,
      message: parsed?.message ?? `BIM-Hub property proposal failed (HTTP ${response.status}).`
    });
  }
  renderDraftLedger();
}

/**
 * Shows the D3 commit payload preview: what a BIM-Hub write-back would contain, built entirely
 * locally from the two proposals above, plus (via `renderD3SaveOutcome`) whatever the last real
 * save attempt against this fixture actually did. Rendered every time `renderDraftLedger` runs,
 * regardless of whether Pending changes is currently empty, so a Codes-only classification proposal
 * is still reviewable here.
 */
function renderD3CommitPayloadReview(container: HTMLElement): void {
  const section = document.createElement("section");
  section.className = "d3-commit-review";
  const heading = document.createElement("div");
  heading.className = "d3-commit-review-heading";
  const strong = document.createElement("strong");
  strong.textContent = "BIM-Hub commit review";
  heading.append(strong);
  section.append(heading);

  const sendButton = document.createElement("button");
  sendButton.type = "button";
  sendButton.disabled = true;

  const saveOutcome = renderD3SaveOutcome();

  if (!currentEnrichmentBinding.bound || !currentClassificationBinding.bound) {
    sendButton.textContent = "Send to BIM-Hub";
    sendButton.title = "Open and observe a local IFC first.";
    const unbound = document.createElement("p");
    unbound.textContent = "Open and observe a local IFC to preview a commit payload. No digest is fabricated while unbound.";
    section.append(unbound);
    if (saveOutcome !== null) section.append(saveOutcome);
    section.append(sendButton);
    container.append(section);
    return;
  }
  if (currentD3CommitPayload === null) {
    sendButton.textContent = "Send to BIM-Hub";
    sendButton.title = "Computing commit payload preview…";
    const computing = document.createElement("p");
    computing.textContent = "Computing commit payload preview…";
    section.append(computing);
    if (saveOutcome !== null) section.append(saveOutcome);
    section.append(sendButton);
    container.append(section);
    return;
  }
  if (currentD3CommitPayload.status === "commit-payload-refused") {
    sendButton.textContent = "Send to BIM-Hub";
    sendButton.title = "Refused as a whole commit; nothing was built to save.";
    const refusal = currentD3CommitPayload;
    const refused = document.createElement("p");
    refused.textContent = `Refused as a whole commit: ${refusal.reason} (${refusal.refusedProposalKinds.join(", ")}). ` +
      "No commit payload was built for any proposal — all-or-nothing, matching BIM-Hub's own CAS commit.";
    section.append(refused);
    if (saveOutcome !== null) section.append(saveOutcome);
    section.append(sendButton);
    container.append(section);
    return;
  }

  const payload = currentD3CommitPayload;
  const counts = document.createElement("dl");
  counts.className = "d3-commit-review-counts";
  const rows: readonly (readonly [string, string | number])[] = [
    ["Status", payload.status],
    ["Base revision", payload.baseRevision.known
      ? `${payload.baseRevision.projectId}/${payload.baseRevision.modelId}@${payload.baseRevision.revision}`
      : "unknown (no BIM-Hub ModelRevision pin yet)"],
    ["Added", payload.changeSummary.addedCount],
    ["Modified", payload.changeSummary.modifiedCount],
    ["Removed", payload.changeSummary.removedCount],
    ["Total operations", payload.changeSummary.totalOperations],
    ["Manifest SHA-256", payload.manifestSha256],
    ["Payload SHA-256", payload.payloadSha256],
    ["Idempotency key", payload.idempotencyKey]
  ];
  for (const [label, value] of rows) {
    const term = document.createElement("dt");
    term.textContent = label;
    const description = document.createElement("dd");
    description.textContent = String(value);
    counts.append(term, description);
  }
  section.append(counts);

  if (payload.status !== "commit-payload") {
    sendButton.textContent = "Send to BIM-Hub";
    sendButton.title = "Nothing to save yet — the payload preview has no operations.";
  } else if (d3SaveState === "saving") {
    sendButton.textContent = "Saving…";
    sendButton.disabled = true;
  } else {
    sendButton.textContent = d3SaveState === "saved" || loadKnownD3SaveTarget(currentFixture.id) !== null
      ? "Save new revision to BIM-Hub"
      : "Save to BIM-Hub";
    sendButton.disabled = false;
    sendButton.title = "";
    sendButton.addEventListener("click", () => {
      void attemptD3Save(currentFixture, payload);
    });
  }

  if (saveOutcome !== null) section.append(saveOutcome);
  section.append(sendButton);
  container.append(section);
}

function scheduleEnrichmentProposalRefresh(): void {
  const binding = localSourceBinding(currentLocalIfcObservation);
  const signature = JSON.stringify({ binding, fixtureId: currentFixture.id, entries: state.draft.entries });
  if (signature === enrichmentProposalSignature) return;
  enrichmentProposalSignature = signature;
  currentEnrichmentBinding = binding;
  const token = ++enrichmentProposalToken;
  if (!binding.bound) {
    currentEnrichmentProposal = null;
    renderDraftLedger();
    return;
  }
  void authorLocalEnrichmentProposalFromDraft(currentFixture, state.draft, binding)
    .then((result: LocalProposalBinding<D2LocalEnrichmentProposal>) => {
      if (token !== enrichmentProposalToken) return;
      currentEnrichmentProposal = result.bound ? result.proposal : null;
      renderDraftLedger();
    })
    .catch(() => {
      if (token !== enrichmentProposalToken) return;
      currentEnrichmentProposal = null;
      renderDraftLedger();
    });
}

function scheduleClassificationProposalRefresh(): void {
  const binding = localSourceBinding(currentLocalIfcObservation);
  const signature = JSON.stringify({
    binding,
    fixtureId: currentFixture.id,
    staged: [...classificationStagedAfter.entries()].sort(([left], [right]) => left.localeCompare(right))
  });
  if (signature === classificationProposalSignature) return;
  classificationProposalSignature = signature;
  currentClassificationBinding = binding;
  const token = ++classificationProposalToken;
  if (!binding.bound) {
    currentClassificationProposal = null;
    renderDraftLedger();
    return;
  }
  void authorLocalClassificationProposalFromStaged(currentFixture, classificationStagedAfter, binding)
    .then((result: LocalProposalBinding<D2LocalClassificationProposal>) => {
      if (token !== classificationProposalToken) return;
      currentClassificationProposal = result.bound ? result.proposal : null;
      renderDraftLedger();
    })
    .catch(() => {
      if (token !== classificationProposalToken) return;
      currentClassificationProposal = null;
      renderDraftLedger();
    });
}

/**
 * Builds the D3 commit payload preview once both the enrichment and classification proposals
 * above have resolved against the same bound source. This never calls the network: it only
 * assembles the deterministic payload so the review panel can show what a commit *would* contain.
 * Workbench has no real BIM-Hub ModelRevision pin for a local IFC source today, so `baseRevision`
 * is always the explicit `{ known: false }` case (see `domain/d3-commit-payload.ts`).
 */
function scheduleD3CommitPayloadRefresh(): void {
  const ready = currentEnrichmentBinding.bound && currentClassificationBinding.bound &&
    currentEnrichmentProposal !== null && currentClassificationProposal !== null;
  const signature = JSON.stringify({
    fixtureId: currentFixture.id,
    ready,
    enrichmentBinding: currentEnrichmentBinding,
    classificationBinding: currentClassificationBinding,
    enrichmentProposal: currentEnrichmentProposal,
    classificationProposal: currentClassificationProposal
  });
  if (signature === d3CommitPayloadSignature) return;
  d3CommitPayloadSignature = signature;
  const token = ++d3CommitPayloadToken;
  if (!ready || !currentEnrichmentBinding.bound) {
    currentD3CommitPayload = null;
    renderDraftLedger();
    return;
  }
  const proposals: readonly D3ProposalEntryInput[] = [
    { kind: "local-enrichment", proposal: currentEnrichmentProposal! },
    { kind: "local-classification", proposal: currentClassificationProposal! }
  ];
  void buildD3CommitPayload({
    source: {
      datasetId: currentFixture.id,
      byteLength: currentEnrichmentBinding.source.byteLength,
      sha256: currentEnrichmentBinding.source.sha256
    },
    baseRevision: { known: false },
    proposals
  })
    .then((result) => {
      if (token !== d3CommitPayloadToken) return;
      currentD3CommitPayload = result;
      renderDraftLedger();
    })
    .catch(() => {
      if (token !== d3CommitPayloadToken) return;
      currentD3CommitPayload = null;
      renderDraftLedger();
    });
}

/**
 * Mirrors the in-memory `state.draft` to the device-local SQLite recovery cache when it changed.
 * `state.draft` remains the single live source of truth while the app is running; a failed
 * background persist only means a future restart cannot resume this exact draft, so it is logged
 * and never surfaced as a blocking error (see `integration/local-draft-persistence.ts`).
 */
function scheduleDraftPersistence(): void {
  if (!nativeLocalShell) return;
  const signature = JSON.stringify({ fixtureId: currentFixture.id, entries: state.draft.entries });
  if (signature === draftPersistenceSignature) return;
  draftPersistenceSignature = signature;
  const token = ++draftPersistenceToken;
  void persistNativeDraftLedger(state.draft).catch((error: unknown) => {
    if (token !== draftPersistenceToken) return;
    console.warn("Device-local pending-changes backup failed.", error);
  });
}

/** Restores a previously persisted Draft ledger for `fixture`, if any, after `activateFixture` reset it to empty. */
function loadNativeDraftLedgerForFixture(fixture: AnonymousFixture): void {
  if (!nativeLocalShell) return;
  const token = ++draftPersistenceToken;
  void loadNativeDraftLedger(fixture.id)
    .then((loaded) => {
      if (token !== draftPersistenceToken || loaded === null || fixture.id !== currentFixture.id) return;
      state.draft = loaded;
      draftPersistenceSignature = JSON.stringify({ fixtureId: loaded.fixtureId, entries: loaded.entries });
      renderDraftLedger();
    })
    .catch((error: unknown) => {
      console.warn("Device-local pending-changes restore failed.", error);
    });
}

function renderDraftLedger(): void {
  scheduleEnrichmentProposalRefresh();
  scheduleClassificationProposalRefresh();
  scheduleD3CommitPayloadRefresh();
  scheduleDraftPersistence();
  draftLedger.replaceChildren();
  const heading = document.createElement("div");
  heading.className = "draft-heading";
  const title = document.createElement("div");
  const strong = document.createElement("strong");
  strong.textContent = `Pending changes · ${state.draft.entries.length}`;
  const note = document.createElement("span");
  note.textContent = "Local session · source IFC unchanged";
  title.append(strong, note);
  const clear = document.createElement("button");
  clear.type = "button";
  clear.textContent = "Clear changes";
  clear.disabled = state.draft.entries.length === 0;
  clear.addEventListener("click", () => {
    state.draft = emptyDraftLedger(currentFixture.id);
    resetLocalExport("Pending changes cleared. The previously regenerated output was discarded.");
    state.bulkPropertyAction = "";
    proposalOutput.textContent = "Pending changes cleared. Source observation remains unchanged.";
    proposalOutput.dataset.state = "idle";
    if (nativeLocalShell) {
      draftPersistenceSignature = null;
      const clearedFixtureId = currentFixture.id;
      void clearNativeDraftLedger(clearedFixtureId).catch((error: unknown) => {
        console.warn("Device-local pending-changes backup clear failed.", error);
      });
    }
    render();
    // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
    renderDraftLedger();
  });
  heading.append(title, clear);
  draftLedger.append(heading);

  renderLocalProposalSummary(
    draftLedger,
    "Local enrichment proposal",
    currentEnrichmentBinding,
    currentEnrichmentProposal,
    "Existing occurrence property replace is writer-supported; add, clear, and remove are retained here as typed unsupported operations, never applied or shown as saved."
  );
  renderLocalProposalSummary(
    draftLedger,
    "Local classification proposal",
    currentClassificationBinding,
    currentClassificationProposal,
    "No catalog authority is claimed. The current writer treats every classification mapping as unsupported and unapplied."
  );

  renderD3CommitPayloadReview(draftLedger);

  if (state.draft.entries.length === 0) {
    const empty = document.createElement("p");
    empty.textContent = "Edit selected properties to review their original and proposed values here.";
    draftLedger.append(empty);
    renderLocalExportDialog();
    return;
  }
  const list = document.createElement("div");
  list.className = "draft-list";
  for (const entry of state.draft.entries.slice(0, 200)) {
    const row = document.createElement("div");
    row.className = "draft-row";
    const values = document.createElement("div");
    const label = document.createElement("strong");
    label.textContent = `${entry.elementId} · ${entry.property}`;
    const diff = document.createElement("span");
    diff.textContent = `${entry.operation} · ${displayValue(entry.before)} → ${displayValue(entry.after)} · ${entry.source}`;
    values.append(label, diff);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = "Remove";
    remove.addEventListener("click", () => {
      state.draft = removeDraftChange(state.draft, entry.elementId, entry.property);
      resetLocalExport("Pending changes changed. Regenerate IFC again before download.");
      proposalOutput.textContent = JSON.stringify(state.draft, null, 2);
      render();
      // See stageBulkPropertyDraft's identical call for why this is required, not redundant.
      renderDraftLedger();
    });

    // D2-C property proposal -> BIM-Hub: one entry-scoped button, distinct from the whole-draft
    // "Send to BIM-Hub" in renderD3CommitPayloadReview above. Needs an existing base_revision to
    // propose against (loadKnownD3SaveTarget with a real revision -- i.e. at least one D3 save has
    // already succeeded for this fixture) and an entry evaluateDraftEntryForProposal accepts (see
    // that function's own doc comment for exactly which entries qualify: "replace" only, before a
    // bare string/number/boolean). Both gates are re-checked on click too (attemptPropertyProposal
    // refuses closed), this is only the UI-affordance layer.
    const propose = document.createElement("button");
    propose.type = "button";
    propose.textContent = "Propose to BIM-Hub";
    const proposeKey = propertyProposalEntryKey(entry.elementId, entry.property);
    const knownTarget = loadKnownD3SaveTarget(currentFixture.id);
    const eligibility = evaluateDraftEntryForProposal(entry);
    if (knownTarget === null || knownTarget.revision === null) {
      propose.disabled = true;
      propose.title = "Save to BIM-Hub at least once first (below), so a base revision exists to propose a change against.";
    } else if (!eligibility.eligible) {
      propose.disabled = true;
      propose.title = eligibility.reason;
    } else if (propertyProposalSubmitting.has(proposeKey)) {
      propose.textContent = "Proposing…";
      propose.disabled = true;
    } else {
      propose.disabled = false;
      propose.addEventListener("click", () => {
        void attemptPropertyProposal(entry);
      });
    }
    row.append(values, propose, remove);

    const proposalOutcome = propertyProposalOutcomes.get(proposeKey);
    if (proposalOutcome !== undefined) {
      const outcomeElement = document.createElement("p");
      outcomeElement.className = proposalOutcome.ok ? "property-proposal-receipt" : "property-proposal-error";
      outcomeElement.textContent = proposalOutcome.ok
        ? `Proposed to BIM-Hub (${proposalOutcome.status === "committed" ? "new revision" : "already proposed"}): ` +
          `revision ${proposalOutcome.targetRevision}, manifest ${proposalOutcome.manifestSha256}.`
        : `${proposalOutcome.code}: ${proposalOutcome.message}`;
      row.append(outcomeElement);
    }
    list.append(row);
  }
  draftLedger.append(list);
  renderLocalExportDialog();
}

function renderToolPanel(): void {
  toolContent.replaceChildren();
  inspectorTitle.textContent = state.activeTool === "quality"
    ? "Checks"
    : state.activeTool === "components"
      ? "Components"
      : state.activeTool === "codes"
        ? "Codes"
        : state.activeTool === "report"
          ? "Report"
          : "Inspect";
  inspectorPanel.setAttribute("aria-label", inspectorTitle.textContent);
  // Report-only layout: the inspector drawer widens to make the quantity-takeoff table the
  // primary surface (see styles.css `.workspace.report-active`); every other tab keeps the
  // narrow drawer / full-width 3D layout unchanged.
  workspace.classList.toggle("report-active", state.activeTool === "report");
  reportResizeHandle.hidden = state.activeTool !== "report";
  applyCustomReportPanelRect();
  for (const tab of document.querySelectorAll<HTMLButtonElement>("[data-tool]")) {
    const active = tab.dataset.tool === state.activeTool && workspaceShellState.inspectorOpen;
    tab.classList.toggle("active", active);
    tab.setAttribute("aria-pressed", String(active));
  }
  if (state.activeTool === "inspect") renderInspectTool();
  if (state.activeTool === "codes") renderCodesTool();
  if (state.activeTool === "components") renderComponentsTool();
  if (state.activeTool === "quality") renderQualityTool();
  if (state.activeTool === "report") renderReportTool();
  renderDraftLedger();
}

function render(): void {
  const datasetView = queryFixtureDataset(
    currentDatasetIndex,
    state.filter,
    state.typeFilter,
    state.outcomeFilter,
    state.selectedIds,
    state.request
  );
  currentVisibleElements = datasetView.visibleElements.filter(
    (element) =>
      (currentValidationFocus === null || currentValidationFocus.elementIds.has(element.id)) &&
      (state.hierarchyFocus === null || state.hierarchyFocus.elementIds.has(element.id))
  );
  normalizePrimarySelection();
  renderRows();
  scene.setSelection(state.selectedIds);
  selectedSummary.textContent = `${state.selectedIds.size} selected`;
  requiredElement<HTMLButtonElement>("clear-scene-selection").disabled = state.selectedIds.size === 0;
  renderToolPanel();
}

function renderRuntimeMetrics(): void {
  const metrics = runtimeMeter.snapshot();
  const fields: readonly [string, string][] = [
    ["First paint", formatMilliseconds(metrics.firstPaintMs)],
    ["Last selection response", formatMilliseconds(metrics.lastSelectionMs)],
    ["Worst selection response", formatMilliseconds(metrics.worstSelectionMs)],
    ["Selections measured", String(metrics.selectionCount)],
    ["Heap", formatBytes(metrics.heapBytes)]
  ];
  runtimeMetrics.replaceChildren();
  for (const [label, value] of fields) {
    const term = document.createElement("dt");
    term.textContent = label;
    const description = document.createElement("dd");
    description.textContent = value;
    runtimeMetrics.append(term, description);
  }
}

function currentJsHeapBytes(): number | null {
  const memory = performance as Performance & { readonly memory?: { readonly usedJSHeapSize: number } };
  return memory.memory?.usedJSHeapSize ?? null;
}

function renderLocalShellMetrics(): void {
  localShellMetrics.dataset.state = currentLocalShellMetrics?.lifecycle ?? "idle";
  localShellMetrics.textContent = currentLocalShellMetrics === null
    ? "No local source measurement yet."
    : JSON.stringify(currentLocalShellMetrics, null, 2);
}

window.d0RuntimeMetrics = () => runtimeMeter.snapshot();
window.aecLocalIfcObservation = () => currentLocalIfcObservation;
window.aecLocalShellMetrics = () => currentLocalShellMetrics;
window.aecSceneViewState = () => scene.viewState();
window.aecProjectionCoverage = () => currentProjectionCoverage;
window.aecLocalIfcMeshExtraction = () => currentMeshExtraction;
window.aecLocalIfcMeshAccounting = () => currentMeshAccounting;
window.aecAuthState = () => authFlow.snapshot();

function generateProposalPreview(): void {
  try {
    const proposal = buildCanonicalProposal(currentFixture, state.selectedIds, state.request);
    proposalOutput.textContent = JSON.stringify(proposal, null, 2);
    proposalOutput.dataset.state = "ready";
  } catch (error) {
    proposalOutput.textContent = error instanceof Error ? error.message : "Proposal generation failed.";
    proposalOutput.dataset.state = "error";
  }
}

function meshExtractionDetailText(): string {
  if (currentMeshExtraction === null) return "Tessellated mesh extraction has not run for the active dataset.";
  if (currentMeshExtraction.state === "refused") {
    return `Tessellated mesh extraction (ifc-mesh-wasm ${LOCAL_IFC_MESH_ARTIFACT.version}) was refused for this source: ${currentMeshExtraction.reason}. All renderable records use the plan-derived box fallback.`;
  }
  const accounting = currentMeshAccounting;
  const overwritten =
    currentMeshExtraction.overwrittenGuidCount > 0
      ? ` ${currentMeshExtraction.overwrittenGuidCount} element(s) spanned multiple mesh chunks and kept only the last chunk's geometry (known ifc-mesh-wasm limitation).`
      : "";
  const merged =
    accounting === null
      ? ""
      : ` Merge: ${accounting.meshMatched} mesh-matched, ${accounting.boxFallback} box fallback, ${accounting.unrenderedUnchanged} unrendered unchanged, ${accounting.producerSkippedUnchanged} producer-skipped unchanged.`;
  return `Tessellated mesh extraction (ifc-mesh-wasm ${LOCAL_IFC_MESH_ARTIFACT.version}): ${currentMeshExtraction.chunkCount} chunk(s), ${currentMeshExtraction.retainedTriangleCount} retained triangles of ${currentMeshExtraction.rawTriangleCount} reported.${overwritten}${merged}`;
}

function renderProjectionCoverage(): void {
  const coverage = currentProjectionCoverage;
  sceneCoverage.dataset.state = coverage.state;
  sceneCoverageCompactSummary.textContent = `${coverage.renderable} shown in 3D`;
  const geometryUnavailable = coverage.missingGeometryRecords + coverage.unsupportedGeometryRecords;
  const dataOnly = coverage.nonGeometricRetainedRecords + coverage.unclassifiedRetainedRecords;
  const warning =
    coverage.state === "none"
      ? "No supported geometry is available for the 3D view. Model data remains available in Browse and Inspect."
      : coverage.state === "partial"
        ? "Some model data is available in Browse and Inspect but is not shown in 3D."
        : coverage.state === "empty"
          ? "No model data is available for the 3D view."
          : "All supported geometric elements are shown in 3D.";
  sceneCoverage.replaceChildren();
  const summary = document.createElement("strong");
  summary.textContent = "3D view coverage";
  const description = document.createElement("span");
  description.textContent = warning;
  const metrics = document.createElement("div");
  metrics.className = "coverage-metrics";
  for (const [key, label, value] of [
    ["rendered", "Shown in 3D", coverage.renderable],
    ["rendered-mesh", "Shown via mesh", coverage.meshRenderable],
    ["rendered-box", "Shown via box (fallback)", coverage.boxRenderable],
    ["unavailable", "Geometry unavailable", geometryUnavailable],
    ["data-only", "Data-only records", dataOnly]
  ] as const) {
    const metric = document.createElement("span");
    metric.dataset.metric = key;
    const metricLabel = document.createElement("small");
    metricLabel.textContent = label;
    const metricValue = document.createElement("b");
    metricValue.textContent = String(value);
    metric.append(metricLabel, metricValue);
    metrics.append(metric);
  }
  const details = document.createElement("details");
  details.className = "coverage-technical-details";
  const detailsSummary = document.createElement("summary");
  detailsSummary.textContent = "Technical details";
  const detail = document.createElement("p");
  detail.textContent = `Exact record closure: ${coverage.renderable} shown (${coverage.meshRenderable} mesh + ${coverage.boxRenderable} box) + ${coverage.missingGeometryRecords} missing geometry + ${coverage.unsupportedGeometryRecords} unsupported geometry + ${coverage.nonGeometricRetainedRecords} non-geometric + ${coverage.unclassifiedRetainedRecords} unclassified = ${coverage.records}. Producer-skipped evidence: ${coverage.producerSkippedRecords}.`;
  const meshDetail = document.createElement("p");
  meshDetail.textContent = meshExtractionDetailText();
  details.append(detailsSummary, detail, meshDetail);
  sceneCoverage.append(summary, description, metrics, details);
}

function activateFixture(fixture: AnonymousFixture, quality: LocalIfcProjection["quality"] | null): void {
  clearCustomReportPanelRect();
  currentFixture = fixture;
  activeSourceLabel.textContent = fixture.label;
  currentDatasetIndex = createFixtureDatasetIndex(fixture.elements);
  currentQuality = quality;
  currentQualitySummary = summarizeQualityIssues(quality?.issues ?? [], fixture.elements);
  currentProjectionCoverage = summarizeProjectionCoverage(fixture.elements);
  currentValidationResult = null;
  currentValidationOverview = null;
  currentValidationTypeSummary = summarizeLocalValidationByType(fixture.elements);
  currentHierarchy = buildInspectionHierarchy(fixture.id, fixture.label, fixture.elements);
  expandedHierarchyIds = new Set([currentHierarchy.id, ...currentHierarchy.children.map((scope) => scope.id)]);
  // A grouping rule referencing a property that does not exist on the new fixture is dropped
  // rather than silently kept (which would make every element fall into a "(missing)" bucket).
  const availableRules = availableGroupingKeys(fixture.elements);
  const reconciledRules = reportGroupingRules.filter((id) => availableRules.some((descriptor) => descriptor.id === id));
  reportGroupingRules = reconciledRules.length > 0 ? reconciledRules : DEFAULT_REPORT_GROUPING_RULES;
  // Same reconciliation for display columns: a property that does not exist on the new fixture is
  // dropped rather than silently kept (which would report every group's column as absent).
  const availableColumns = availableDisplayColumns(fixture.elements);
  reportDisplayColumns = reportDisplayColumns.filter((name) => availableColumns.includes(name));
  currentReport = buildReport(
    fixture.id,
    fixture.label,
    fixture.elements,
    currentLocalIfcObservation,
    reportGroupingRules,
    reportDisplayColumns
  );
  currentValidationFocus = null;
  state.draft = emptyDraftLedger(fixture.id);
  draftPersistenceSignature = null;
  resetLocalExport();
  classificationStagedAfter = new Map();
  currentEnrichmentProposal = null;
  enrichmentProposalSignature = null;
  currentClassificationProposal = null;
  classificationProposalSignature = null;
  currentD3CommitPayload = null;
  d3CommitPayloadSignature = null;
  state.qualitySeverity = "all";
  state.qualityAction = "";
  state.bulkPropertyAction = "";
  state.classificationAction = "";
  state.selectedIds.clear();
  state.primarySelectedId = null;
  state.selectionAnchorId = null;
  state.filter = "";
  state.typeFilter = null;
  state.outcomeFilter = null;
  state.hierarchyFocus = null;
  filterInput.value = "";
  table.scrollTop = 0;
  scene.setElements(currentFixture.elements);
  syncRestoreViewButton();
  const renderableCount = currentFixture.elements.filter((element) => element.geometry !== null).length;
  sceneSummary.textContent = `${renderableCount} renderable / ${currentFixture.elements.length} records`;
  renderProjectionCoverage();
  renderImportDiagnostics();
  renderHierarchy();
  render();
  loadNativeDraftLedgerForFixture(fixture);
}

filterInput.addEventListener("input", () => {
  state.filter = filterInput.value;
  state.typeFilter = null;
  state.hierarchyFocus = null;
  currentValidationFocus = null;
  renderHierarchy();
  table.scrollTop = 0;
  render();
});
propertyInput.addEventListener("input", () => {
  state.request.property = propertyInput.value;
  render();
});
valueInput.addEventListener("input", () => {
  state.request.value = valueInput.value;
  render();
});
table.addEventListener("scroll", renderRows, { passive: true });
requiredElement<HTMLButtonElement>("clear-selection").addEventListener("click", () => {
  state.selectedIds.clear();
  state.primarySelectedId = null;
  state.selectionAnchorId = null;
  state.bulkPropertyAction = "";
  render();
});
requiredElement<HTMLButtonElement>("clear-scene-selection").addEventListener("click", () => {
  requiredElement<HTMLButtonElement>("clear-selection").click();
});
requiredElement<HTMLButtonElement>("navigate-mode").addEventListener("click", () => setSceneInteractionMode("navigate"));
requiredElement<HTMLButtonElement>("pan-mode").addEventListener("click", () => setSceneInteractionMode("pan"));
requiredElement<HTMLButtonElement>("select-mode").addEventListener("click", () => setSceneInteractionMode("select"));
requiredElement<HTMLButtonElement>("frame-all").addEventListener("click", () => {
  scene.frameAll();
  syncRestoreViewButton();
});
requiredElement<HTMLButtonElement>("restore-view").addEventListener("click", () => {
  scene.restorePreviousView();
  syncRestoreViewButton();
});
requiredElement<HTMLButtonElement>("select-visible").addEventListener("click", selectVisibleElements);
requiredElement<HTMLButtonElement>("preview").addEventListener("click", generateProposalPreview);
requiredElement<HTMLButtonElement>("stage-draft").addEventListener("click", () => {
  stageTrackedDraft(propertyInput.value, valueInput.value, "property");
});
for (const tab of document.querySelectorAll<HTMLButtonElement>("[data-tool]")) {
  tab.addEventListener("click", () => {
    const tool = tab.dataset.tool;
    if (tool === "inspect" || tool === "codes" || tool === "components" || tool === "quality" || tool === "report") {
      if (state.activeTool === tool && workspaceShellState.inspectorOpen) {
        workspaceShellState = toggleWorkspacePane(workspaceShellState, "inspector", workspaceLayout);
        renderWorkspaceShell();
        renderToolPanel();
        return;
      }
      state.activeTool = tool;
      workspaceShellState = openInspectorForTool(workspaceShellState, workspaceLayout);
      renderWorkspaceShell();
      renderToolPanel();
    }
  });
}
requiredElement<HTMLButtonElement>("toggle-browser").addEventListener("click", () => {
  workspaceShellState = toggleWorkspacePane(workspaceShellState, "browser", workspaceLayout);
  renderWorkspaceShell();
  renderToolPanel();
});

// The Report panel can be moved and resized like a floating window, matching Qonic's Report panel
// (kaz, 2026-08-11). Every other tab keeps the fixed docked layout; a custom rect only ever applies
// while the Report tab is active and is cleared whenever the source fixture changes so a stale
// position never survives switching datasets.
interface ReportPanelRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}
let customReportPanelRect: ReportPanelRect | null = null;

function positionReportResizeHandle(): void {
  if (reportResizeHandle.hidden) return;
  const box = inspectorPanel.getBoundingClientRect();
  const handleSize = reportResizeHandle.getBoundingClientRect().width || 17.6;
  reportResizeHandle.style.left = `${box.right - handleSize}px`;
  reportResizeHandle.style.top = `${box.bottom - handleSize}px`;
}

function applyCustomReportPanelRect(): void {
  const active = state.activeTool === "report" && customReportPanelRect !== null;
  inspectorPanel.classList.toggle("custom-rect", active);
  if (!active || customReportPanelRect === null) {
    inspectorPanel.style.removeProperty("left");
    inspectorPanel.style.removeProperty("top");
    inspectorPanel.style.removeProperty("width");
    inspectorPanel.style.removeProperty("height");
    positionReportResizeHandle();
    return;
  }
  inspectorPanel.style.left = `${customReportPanelRect.left}px`;
  inspectorPanel.style.top = `${customReportPanelRect.top}px`;
  inspectorPanel.style.width = `${customReportPanelRect.width}px`;
  inspectorPanel.style.height = `${customReportPanelRect.height}px`;
  positionReportResizeHandle();
}

function clearCustomReportPanelRect(): void {
  customReportPanelRect = null;
  applyCustomReportPanelRect();
}

function currentReportPanelRect(): ReportPanelRect {
  const box = inspectorPanel.getBoundingClientRect();
  const workspaceBox = workspace.getBoundingClientRect();
  return { left: box.left - workspaceBox.left, top: box.top - workspaceBox.top, width: box.width, height: box.height };
}

const REPORT_PANEL_MIN_WIDTH = 420;
const REPORT_PANEL_MIN_HEIGHT = 260;

inspectorPanelHeading.addEventListener("pointerdown", (event) => {
  if (state.activeTool !== "report" || event.button !== 0) return;
  if (event.target instanceof Element && event.target.closest("button") !== null) return;
  const start = currentReportPanelRect();
  const startX = event.clientX;
  const startY = event.clientY;
  const workspaceBox = workspace.getBoundingClientRect();
  const pointerId = event.pointerId;
  inspectorPanelHeading.setPointerCapture(pointerId);
  const onMove = (moveEvent: PointerEvent): void => {
    const nextLeft = Math.min(Math.max(0, start.left + (moveEvent.clientX - startX)), Math.max(0, workspaceBox.width - start.width));
    const nextTop = Math.min(Math.max(0, start.top + (moveEvent.clientY - startY)), Math.max(0, workspaceBox.height - start.height));
    customReportPanelRect = { left: nextLeft, top: nextTop, width: start.width, height: start.height };
    applyCustomReportPanelRect();
  };
  const onUp = (): void => {
    inspectorPanelHeading.releasePointerCapture(pointerId);
    inspectorPanelHeading.removeEventListener("pointermove", onMove);
    inspectorPanelHeading.removeEventListener("pointerup", onUp);
  };
  inspectorPanelHeading.addEventListener("pointermove", onMove);
  inspectorPanelHeading.addEventListener("pointerup", onUp);
});

reportResizeHandle.addEventListener("pointerdown", (event) => {
  if (state.activeTool !== "report" || event.button !== 0) return;
  const start = currentReportPanelRect();
  const startX = event.clientX;
  const startY = event.clientY;
  const workspaceBox = workspace.getBoundingClientRect();
  const pointerId = event.pointerId;
  reportResizeHandle.setPointerCapture(pointerId);
  const onMove = (moveEvent: PointerEvent): void => {
    const maxWidth = Math.max(REPORT_PANEL_MIN_WIDTH, workspaceBox.width - start.left - 12);
    const maxHeight = Math.max(REPORT_PANEL_MIN_HEIGHT, workspaceBox.height - start.top - 12);
    const nextWidth = Math.min(Math.max(REPORT_PANEL_MIN_WIDTH, start.width + (moveEvent.clientX - startX)), maxWidth);
    const nextHeight = Math.min(Math.max(REPORT_PANEL_MIN_HEIGHT, start.height + (moveEvent.clientY - startY)), maxHeight);
    customReportPanelRect = { left: start.left, top: start.top, width: nextWidth, height: nextHeight };
    applyCustomReportPanelRect();
  };
  const onUp = (): void => {
    reportResizeHandle.releasePointerCapture(pointerId);
    reportResizeHandle.removeEventListener("pointermove", onMove);
    reportResizeHandle.removeEventListener("pointerup", onUp);
  };
  reportResizeHandle.addEventListener("pointermove", onMove);
  reportResizeHandle.addEventListener("pointerup", onUp);
});
requiredElement<HTMLButtonElement>("close-browser").addEventListener("click", () => {
  if (workspaceShellState.browserOpen) {
    workspaceShellState = toggleWorkspacePane(workspaceShellState, "browser", workspaceLayout);
    renderWorkspaceShell();
    renderToolPanel();
  }
});
requiredElement<HTMLButtonElement>("close-inspector").addEventListener("click", () => {
  if (workspaceShellState.inspectorOpen) {
    workspaceShellState = toggleWorkspacePane(workspaceShellState, "inspector", workspaceLayout);
    renderWorkspaceShell();
    renderToolPanel();
  }
});

requiredElement<HTMLButtonElement>("restore-fixture").addEventListener("click", () => {
  currentLocalIfcObservation = null;
  currentLocalSource = null;
  currentMeshExtraction = null;
  currentMeshAccounting = null;
  resetLocalExport();
  if (currentLocalShellMetrics !== null) {
    currentLocalShellMetrics = {
      ...currentLocalShellMetrics,
      lifecycle: "cleared",
      jsHeapBytes: currentJsHeapBytes(),
      sourceBytesRetained: 0
    };
  }
  localIfcFileInput.value = "";
  activateFixture(anonymousFixture, null);
  sceneSummary.textContent = `${anonymousFixture.elements.length} simplified fixture boxes`;
  localIfcProjectionState.textContent = "Anonymous sample fixture active. The local observation and selected source were cleared.";
  localIfcOutput.dataset.state = "idle";
  localIfcOutput.textContent = "Local UI-session observation cleared.";
  renderLocalShellMetrics();
});

async function observeSelectedLocalSource(source: SelectedLocalSource): Promise<void> {
  currentLocalSource = null;
  resetLocalExport("A new source is being observed. Any previous regenerated output was discarded.");
  localIfcOutput.dataset.state = "working";
  localIfcOutput.textContent = "Loading the pinned read-only importer in this local UI session…";
  localShellMetrics.dataset.state = "working";
  localShellMetrics.textContent = "Measuring local read, importer initialization, observation, projection, and retained memory…";
  localOpenCount += 1;
  let importerInitializationMs = 0;
  let observationMs = 0;
  let projectionMs: number | null = null;
  let meshExtractionMs: number | null = null;
  let wasmLinearMemoryBytes: number | null = null;
  let paritySnapshot: T0ParitySnapshot | null = null;
  let lifecycle: LocalShellMetrics["lifecycle"] = "refused";
  currentMeshExtraction = null;
  currentMeshAccounting = null;
  try {
    const importerStartedAt = performance.now();
    const importer = await DEFAULT_IFC_CAPABILITY_PROVIDER.loadImporter();
    importerInitializationMs = performance.now() - importerStartedAt;
    const observationStartedAt = performance.now();
    const observation = await DEFAULT_IFC_CAPABILITY_PROVIDER.observe(importer, source);
    observationMs = performance.now() - observationStartedAt;
    wasmLinearMemoryBytes = importer.memoryBytes?.() ?? null;
    currentLocalIfcObservation = observation;
    localIfcOutput.textContent = JSON.stringify(localIfcDisplayReceipt(observation), null, 2);
    if (observation.state === "observed") {
      lifecycle = "observed";
      try {
        await yieldBrowserFrame();
        const projectionStartedAt = performance.now();
        const projection = DEFAULT_IFC_CAPABILITY_PROVIDER.project(observation);
        projectionMs = performance.now() - projectionStartedAt;

        // Additive: same source bytes the plan importer above already observed, tried again through
        // the separately-pinned ifc-mesh-wasm engine. Never throws (extractLocalIfcMesh returns a
        // typed refusal); the outer try/catch is only a defensive backstop so the existing
        // plan-derived box pipeline can never be broken by this addition.
        const meshStartedAt = performance.now();
        let renderFixture = projection.fixture;
        try {
          const meshOutcome = await extractLocalIfcMesh(source.bytes);
          currentMeshExtraction = meshOutcome;
          if (meshOutcome.state === "extracted") {
            const merged = mergeMeshGeometryIntoFixture(projection.fixture, meshOutcome.geometryByGuid);
            renderFixture = merged.fixture;
            currentMeshAccounting = merged.accounting;
          }
        } catch (meshError) {
          currentMeshExtraction = {
            state: "refused",
            reason: meshError instanceof Error ? meshError.message : "Mesh extraction failed unexpectedly."
          };
          currentMeshAccounting = null;
        }
        meshExtractionMs = performance.now() - meshStartedAt;

        activateFixture(renderFixture, projection.quality);
        currentLocalSource = { ...source, bytes: new Uint8Array(source.bytes) };
        renderDraftLedger();
        paritySnapshot = await buildT0ParitySnapshot(observation, renderFixture, currentProjectionCoverage);
        const renderableCount = renderFixture.elements.filter((element) => element.geometry !== null).length;
        const meshRenderedCount = renderFixture.elements.filter((element) => element.geometry?.kind === "mesh").length;
        localIfcProjectionState.textContent = `${renderFixture.elements.length} records retained; ${renderableCount} rendered (${meshRenderedCount} tessellated mesh, ${renderableCount - meshRenderedCount} plan-derived box); ${projection.quality.unsupportedProjectionCount} records remain outside the current 3D projection; ${projection.quality.issues.length} import diagnostic records.`;
      } catch (projectionError) {
        localIfcProjectionState.textContent = `Observation retained, but inspection projection refused: ${projectionError instanceof Error ? projectionError.message : "unknown projection error"}`;
      }
      localIfcOutput.dataset.state = "ready";
    } else {
      localIfcOutput.dataset.state = "error";
      localIfcProjectionState.textContent = "The importer refused this source; the current inspection dataset was not replaced.";
    }
  } catch (error) {
    localIfcOutput.dataset.state = "error";
    localIfcOutput.textContent = JSON.stringify(
      {
        state: "refused",
        reason: error instanceof Error ? error.message : "Pinned local IFC importer could not initialize.",
        importer: LOCAL_IFC_ARTIFACT
      },
      null,
      2
    );
    localIfcProjectionState.textContent = "Local observation failed; the current inspection dataset was not replaced.";
  } finally {
    currentLocalShellMetrics = {
      shell: source.shell,
      lifecycle,
      openCount: localOpenCount,
      fileReadMs: Number(source.fileReadMs.toFixed(2)),
      importerInitializationMs: Number(importerInitializationMs.toFixed(2)),
      observationMs: Number(observationMs.toFixed(2)),
      projectionMs: projectionMs === null ? null : Number(projectionMs.toFixed(2)),
      meshExtractionMs: meshExtractionMs === null ? null : Number(meshExtractionMs.toFixed(2)),
      wasmLinearMemoryBytes,
      jsHeapBytes: currentJsHeapBytes(),
      sourceBytesRetained: currentLocalSource?.bytes.byteLength ?? 0,
      paritySnapshot
    };
    renderLocalShellMetrics();
  }
}

async function chooseAndObserveNativeIfc(): Promise<void> {
  setIfcLoading(true);
  try {
    localIfcOutput.dataset.state = "working";
    localIfcOutput.textContent = "Waiting for a local IFC selection…";
    const source = await pickNativeIfcSource();
    if (source === null) {
      localIfcOutput.dataset.state = "idle";
      localIfcOutput.textContent = "File selection cancelled. The current inspection dataset is unchanged.";
      return;
    }
    await observeSelectedLocalSource(source);
  } finally {
    setIfcLoading(false);
  }
}

const observeLocalIfcButton = requiredElement<HTMLButtonElement>("observe-local-ifc");
observeLocalIfcButton.addEventListener("click", async () => {
  try {
    if (nativeLocalShell) {
      await chooseAndObserveNativeIfc();
      return;
    }

    setIfcLoading(true);
    const file = localIfcFileInput.files?.item(0);
    if (file === null || file === undefined) {
      localIfcOutput.dataset.state = "error";
      localIfcOutput.textContent = "Select a local IFC file before observation.";
      return;
    }
    await observeSelectedLocalSource(await sourceFromBrowserFile(file));
  } catch (error) {
    localIfcOutput.dataset.state = "error";
    localIfcOutput.textContent = error instanceof Error ? error.message : "The local source could not be read.";
    localIfcProjectionState.textContent = "The current inspection dataset is unchanged.";
  } finally {
    if (!nativeLocalShell) setIfcLoading(false);
  }
});

requiredElement<HTMLButtonElement>("open-ifc").addEventListener("click", () => {
  if (nativeLocalShell) {
    void chooseAndObserveNativeIfc().catch((error: unknown) => {
      localIfcOutput.dataset.state = "error";
      localIfcOutput.textContent = error instanceof Error ? error.message : "The local source could not be read.";
      localIfcProjectionState.textContent = "The current inspection dataset is unchanged.";
    });
    return;
  }
  setIfcLoading(true);
  observeAfterHeaderPick = true;
  localIfcFileInput.value = "";
  localIfcFileInput.click();
});

window.addEventListener("keydown", (event) => {
  if (!nativeLocalShell || !isNativeOpenShortcut(event) || localIfcOutput.dataset.state === "working") return;
  event.preventDefault();
  void chooseAndObserveNativeIfc().catch((error: unknown) => {
    localIfcOutput.dataset.state = "error";
    localIfcOutput.textContent = error instanceof Error ? error.message : "The local source could not be read.";
    localIfcProjectionState.textContent = "The current inspection dataset is unchanged.";
  });
});

localIfcFileInput.addEventListener("change", () => {
  localIfcOutput.dataset.state = "idle";
  localIfcOutput.textContent = "Local source selected. No observation has been created yet.";
  if (observeAfterHeaderPick) {
    observeAfterHeaderPick = false;
    if (localIfcFileInput.files?.item(0) != null) {
      setIfcLoading(false);
      observeLocalIfcButton.click();
    } else setIfcLoading(false);
  }
});
localIfcFileInput.addEventListener("cancel", () => {
  observeAfterHeaderPick = false;
  setIfcLoading(false);
  localIfcOutput.dataset.state = "idle";
  localIfcOutput.textContent = "File selection cancelled. The current inspection dataset is unchanged.";
});

window.addEventListener(
  "beforeunload",
  () => {
    currentLocalIfcObservation = null;
    currentLocalSource = null;
    currentRegeneratedIfc = null;
    scene.dispose();
  },
  { once: true }
);
renderProjectionCoverage();
renderImportDiagnostics();
renderHierarchy();
renderWorkspaceShell();
setSceneInteractionMode(sceneInteractionMode);
render();
renderAuthState();
renderRuntimeMetrics();
renderLocalShellMetrics();
requestAnimationFrame(() => {
  runtimeMeter.recordFirstPaint(applicationStartedAt);
  renderRuntimeMetrics();
});
